import {
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { Dialog as DialogPrimitive } from "radix-ui";
import { Command as CommandPrimitive } from "cmdk";
import type { Icon } from "@phosphor-icons/react";
import {
  SignOutIcon,
  ChatCircleIcon,
  BellIcon,
  GearIcon,
  FilesIcon,
  AddressBookIcon,
  MagnifyingGlassIcon,
  BriefcaseIcon,
  PresentationIcon,
  PencilSimpleIcon,
  GlobeIcon,
  ArrowSquareOutIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import { CommandList } from "~/components/ui/command";
import { Dialog, DialogPortal, DialogTitle, DialogDescription } from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { useIsAdmin, useSuperAdmin } from "~/hooks/queries/use-is-admin";
import { useFeatureFlags, resolveFeatureFlag, resolveDestinationFlag } from "~/hooks/queries/use-feature-flags";
import {
  SIDEBAR_DESTINATIONS,
  SIDEBAR_SECTIONS,
  type SidebarDestination,
  isSidebarDestinationVisible,
  getSidebarDestination,
  destinationSelectAction,
} from "~/components/sidebar-nav-catalog";
import { useProBuilderOpenStore } from "~/lib/stores/pro-builder-open-store";
import {
  SYSTEM_SETTINGS_SECTIONS,
  isSystemSettingsSection,
  systemSettingsPath,
} from "~/lib/system-settings-sections";
import { useChatOpenStore } from "~/lib/stores/chat-open-store";
import { useCodingOpenStore } from "~/lib/stores/coding-open-store";
import { useOpenSettings, useOpenSystemSettings } from "~/features/settings/use-open-settings";
import { originToReturnTo } from "~/lib/chat-origin";
import { useChatSearch, type ChatSearchHit } from "~/hooks/queries/use-chats";
import { useFileSearch, useFileDetail, type FileMeta } from "~/hooks/queries/use-files";
import { useDocumentSearch, useDocumentDetail, type DocumentMeta } from "~/hooks/queries/use-documents";
import { useAppsSearch, type AppSearchHit } from "~/hooks/queries/use-apps-search";
import {
  useRecentActivity,
  type RecentActivity,
  type RecentChatHit,
  type RecentProjectHit,
  type RecentKnowledgeHit,
  type RecentAppHit,
} from "~/hooks/queries/use-recents";
import { useFeatureFlag, SKETCHES_FLAG, SLIDE_DECKS_FLAG } from "~/hooks/queries/use-feature-flags";
import {
  useWorkContainerSearch,
  useMemoryEntitySearch,
  type GraphEntity,
  type GraphEntitySearchHit,
} from "~/hooks/queries/use-graph";
import { uploadHasTextBody, rawFileUrl } from "~/features/chat/modal-asset";
import { shouldSplitFileActions } from "~/components/palette-file-actions";
// The excerpt parser, its markdown clean-up and the two search timings live in
// `~/lib/search-snippet` so the phone surface's sheet search can share them
// without importing this whole module. Re-exported below: the palette is where
// they were born, and its own tests still import them from here.
import {
  CONTENT_SEARCH_DEBOUNCE_MS,
  MIN_CONTENT_QUERY_LENGTH,
  cleanSnippetText,
  parseSnippet,
  type SnippetSegment,
} from "~/lib/search-snippet";
import { useMediaQuery } from "~/hooks/use-media-query";
import { entityTypeLabel } from "~/lib/entity-types";
import { buildHash } from "~/lib/memory-hash";
import type { EntityView } from "~/features/memory/entity-list";
import { formatDate } from "~/lib/local-datetime";
import { useBackdropDismiss } from "~/hooks/use-backdrop-dismiss";
import {
  EmptyPreview,
  HighlightedQueryText,
  HighlightedTitle,
  OPEN_AFFORDANCE_CLASS,
  OpenAffordance,
  PalettePill,
  PaletteRow,
  PaletteSection,
  PreviewByline,
  PreviewEyebrow,
  PreviewTitle,
  RowMeta,
  splitByQuery,
} from "~/components/command-palette-parts";
import {
  BUILD_KINDS_AFTER_APPS,
  BUILD_KINDS_BEFORE_APPS,
  BuildItemPreview,
  BuildItemSection,
  EMPTY_BUILD_ITEMS,
  buildValues,
  countBuildItems,
  filterBuildItems,
  findBuildItem,
  isBuildItemValue,
  type BuildItem,
  type BuildKind,
} from "~/components/command-palette-build-items";
import { usePaletteBuildSources } from "~/components/command-palette-build-sources";
import { openServedApp as openServedAppTab } from "~/lib/open-served-app";

const SECTION_LABEL_BY_ID = new Map(SIDEBAR_SECTIONS.map((s) => [s.id, s.label]));

/**
 * A "Navigate" row's shape, common to a sidebar destination and a System
 * Settings section (FRONT-2376) — the two merge into one findable list below
 * rather than the palette growing a second, parallel "Navigate" group. `id`
 * stays whatever each source's own catalog calls it (a system-settings id
 * can collide with nothing a sidebar destination uses, since the eight of
 * them were removed from that catalog in the same change that added them
 * here).
 */
interface PaletteNavDestination {
  id: string;
  label: string;
  path: string;
  icon: Icon;
  sectionLabel: string | undefined;
  /** FRONT-3003: carried through from the sidebar catalog so choosing the row opens the window. */
  opensProBuilderWindow?: boolean;
}

/**
 * Resolves a "Go to page" row's id to its destination path — checked first
 * against the sidebar's own catalog, then System Settings' (FRONT-2376's
 * eight sections don't live in `SIDEBAR_DESTINATIONS` any more). Exported so
 * a test can drive this REAL lookup directly rather than a hand-built fake
 * map of ids to paths, which would stay green even if the System Settings
 * fallback below were deleted.
 *
 * FRONT-2376 review (item 4): the preview pane's "Go to page" button
 * (`onOpen={selectedAction}`, built from `actionForValue` below) used to
 * check only `getSidebarDestination`, so it silently did nothing for any of
 * the eight System Settings rows even though the row's own click
 * (`onSelect={go(dest.path)}`, built straight off `visibleDestinations`)
 * always worked.
 */
export function resolvePageDestination(id: string): string | undefined {
  const dest = getSidebarDestination(id);
  if (dest) return dest.path;
  if (isSystemSettingsSection(id)) return systemSettingsPath(id);
  return undefined;
}

/**
 * What choosing a "Go to page" destination does (FRONT-3003): open the Pro
 * Builder window over the current page, or navigate to a path. The row's
 * selection AND the preview pane's "Go to page" button both resolve through
 * this, so the preview can never fall back to the `/pro-builder` deep link
 * (which opens the window but redirects to Home).
 */
export type PageTarget = { kind: "open-pro-builder" } | { kind: "navigate"; path: string };

export function pageTargetForDestination(
  dest: Pick<SidebarDestination, "opensProBuilderWindow" | "path">,
): PageTarget {
  return destinationSelectAction(dest) === "open-pro-builder"
    ? { kind: "open-pro-builder" }
    : { kind: "navigate", path: dest.path };
}

export function resolvePageTarget(id: string): PageTarget | undefined {
  const dest = getSidebarDestination(id);
  if (dest) return pageTargetForDestination(dest);
  const path = resolvePageDestination(id);
  return path ? { kind: "navigate", path } : undefined;
}

/**
 * The eight System Settings sections as palette "Navigate" rows — gated
 * exactly like a sidebar destination's own admin gate
 * (`isSidebarDestinationVisible`, which already applies to Org
 * Settings/Sandbox in the OLD catalog): fail CLOSED while the boot query is
 * still loading, since `isAdmin` defaults to `false` until it resolves.
 * Exported (and taking `isAdmin` as a plain argument rather than calling
 * `useIsAdmin()` itself) so a test can drive it with the value the REAL hook
 * produced, for every state that hook can be in — loading, non-admin, admin —
 * rather than a hand-set boolean that could drift from what the hook chain
 * actually returns (FRONT-2376 review item 10).
 *
 * FRONT-2376 review (item 12): each section's own General/Admin/Health group
 * is an internal RAIL grouping, not a name someone searching the palette
 * would recognize — every row from this catalog is subtitled "System
 * Settings" instead (the merged "Navigate" group has no room for a second,
 * nested heading; see the comment on `PaletteNavDestination`).
 *
 * FRONT-2376 review round 2 (item 10): a row matches when the query hits
 * EITHER its own label ("policy" finds Policy) OR that shared subtitle
 * ("system" finds all eight, the same way it finds the standalone "System
 * Settings" row this file also adds) — matching only the label meant typing
 * "system", the one word naming the destination someone actually landed on,
 * found none of the eight rows at all.
 */
const SYSTEM_SETTINGS_SUBTITLE = "System Settings";

export function buildSystemSettingsDestinations(
  isAdmin: boolean,
  search: string,
  // FRONT-1822: a flag-gated section is offered only when its flag is on.
  // Defaults to "off" so a caller that forgets to pass flags hides it.
  isFlagEnabled: (flag: string) => boolean = () => false,
): PaletteNavDestination[] {
  return SYSTEM_SETTINGS_SECTIONS.filter((s) => {
    const passesGate = !s.gate || isAdmin;
    const passesFlag = !s.flag || isFlagEnabled(s.flag);
    const hasAccess = passesGate && passesFlag;
    const matchesQuery =
      matchesPaletteQuery(s.label, search) || matchesPaletteQuery(SYSTEM_SETTINGS_SUBTITLE, search);
    return hasAccess && matchesQuery;
  }).map((s) => ({
    id: s.id,
    label: s.label,
    path: systemSettingsPath(s.id),
    icon: s.icon,
    sectionLabel: SYSTEM_SETTINGS_SUBTITLE,
  }));
}

const MAX_CONTENT_RESULTS = 5;
// FRONT-874: how long the highlighted row has to stay put before the preview
// pane's lazy detail fetch (document/file content) actually fires. Without
// this, arrowing quickly through the list would fire one network request per
// row it passed over.
const PREVIEW_DETAIL_DEBOUNCE_MS = 150;
// Both the document and file preview excerpts show a bounded peek of the
// body, faded out at the bottom when the real content runs longer. Capped at
// ~360 chars (FRONT-874 review fix) to match the approved mockup's short,
// curated excerpts rather than a full text wall.
const PREVIEW_CONTENT_PEEK_CHARS = 360;
// FRONT-874 review fix: the preview pane column is CSS-hidden below this
// breakpoint (`hidden lg:block` on the pane's wrapper below) — but a `hidden`
// class doesn't stop React from mounting that subtree and firing its
// fetches, so the document/file detail queries additionally gate on this
// matching the pane's own breakpoint exactly.
const PREVIEW_PANE_MEDIA_QUERY = "(min-width: 1024px)";

/**
 * FRONT-2391 — the command palette's "Recent activity" sections: shown ABOVE
 * Navigate whenever the search box is empty, newest first, each hidden when
 * empty. Render order, heading text
 * and the browser-spec-facing `data-testid` are all keyed off this one id
 * list so there is exactly one place that names the six sections — the JSX
 * below and `buildRecentSections`'s own test both read these maps rather
 * than repeating the strings.
 */
const RECENT_SECTION_ORDER = ["chats", "apps", "projects", "knowledge", "sketches", "decks"] as const;
type RecentSectionId = (typeof RECENT_SECTION_ORDER)[number];

/** "Slides" here too, not "Decks" — same product naming `APP_KIND_SECTION_LABEL` uses below. */
const RECENT_SECTION_HEADING: Record<RecentSectionId, string> = {
  chats: "Recent chats",
  apps: "Recent apps",
  projects: "Recent projects",
  knowledge: "Recent knowledge",
  sketches: "Recent sketches",
  decks: "Recent slides",
};

const RECENT_SECTION_TEST_ID: Record<RecentSectionId, string> = {
  chats: "palette-recent-chats",
  apps: "palette-recent-apps",
  projects: "palette-recent-projects",
  knowledge: "palette-recent-knowledge",
  sketches: "palette-recent-sketches",
  decks: "palette-recent-decks",
};

/**
 * Pure: which Recent-activity sections render, in order, with their heading
 * and test id — everything the JSX needs to decide "show it or not" EXCEPT
 * the actual rows (those stay per-kind-typed in the caller, since a chat row
 * and a project row share nothing renderable). A section with zero items is
 * dropped entirely rather than rendered empty (the contract's "hidden when
 * empty" rule). Exported for unit testing.
 */
export function buildRecentSections(
  activity: Pick<RecentActivity, RecentSectionId>,
): Array<{ id: RecentSectionId; heading: string; testId: string; count: number }> {
  return RECENT_SECTION_ORDER.map((id) => ({
    id,
    heading: RECENT_SECTION_HEADING[id],
    testId: RECENT_SECTION_TEST_ID[id],
    count: activity[id].length,
  })).filter((section) => section.count > 0);
}

/**
 * Pure: `openDocument`/`DocumentPreview` (below, in `CommandPalette`) take a
 * full `DocumentMeta`, but the recents wire's knowledge row is deliberately
 * lean (`src/recents/service.ts`'s `RecentKnowledgeWire`) since the palette
 * only needs enough to open and preview it, never to render a search
 * snippet. Fills the fields `openDocument`/`documentToFeedItem`/
 * `DocumentPreview` read but the wire doesn't carry with the row's own
 * `at`/empty defaults.
 *
 * `codingSessionId` rides straight through from the wire (FRONT-2391 gap
 * fix) — it used to be hardcoded `null` here, which meant a coding-session
 * journal opened from Recent knowledge silently lost its resume handle
 * (`openDocument` already branches on this field; only the value reaching it
 * was wrong). Exported for unit testing.
 */
export function recentKnowledgeToDocumentMeta(hit: RecentKnowledgeHit): DocumentMeta {
  return {
    filename: hit.id,
    title: hit.title,
    date: hit.at,
    tags: [],
    summary: "",
    codingSessionId: hit.codingSessionId,
    createdAt: hit.at,
    updatedAt: hit.at,
  };
}

/**
 * FRONT-2391 product correction — a Recent-chats row is one line: the title,
 * then the first-message preview folded onto the SAME line in muted text
 * (never a second, wrapped block — see `RecentChatsSection` below). A chat
 * with no title shows its preview alone, standing in for the title; a chat
 * with neither falls back to the same "Untitled chat" text the row always
 * showed. Plain JSX text interpolation throughout — never
 * `dangerouslySetInnerHTML` — so a literal "<b>" or markdown-looking
 * character in someone's actual first message renders untouched, same as
 * before this row folded onto one line. Exported for unit testing.
 */
export function recentChatRowTitle(hit: RecentChatHit): ReactNode {
  if (!hit.title) return hit.preview ?? "Untitled chat";
  if (!hit.preview) return hit.title;
  return (
    <>
      {hit.title}
      <span className="font-normal text-muted-foreground"> · {hit.preview}</span>
    </>
  );
}

/**
 * Pure: case-insensitive substring match, used to manually filter the
 * palette's static groups (Navigate/Account) now that `shouldFilter`
 * is off on the `Command` root — cmdk's own fuzzy filter can't be reused
 * once the list also contains async content-search results it knows nothing
 * about. Empty (or whitespace-only) query matches everything. Exported for
 * unit testing.
 */
export function matchesPaletteQuery(label: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return label.toLowerCase().includes(q);
}

// `splitByQuery` moved to `command-palette-parts` (FRONT-2436); re-exported so
// the palette's own tests keep importing it from here.
export { splitByQuery };

// Re-exported so this module stays the one place the palette's own tests and
// call sites import them from, even though they now live in `~/lib/search-snippet`.
export { parseSnippet, cleanSnippetText, MIN_CONTENT_QUERY_LENGTH, CONTENT_SEARCH_DEBOUNCE_MS };
export type { SnippetSegment };

/**
 * Pure: true while the immediate query is long enough to trigger content
 * search but the debounced value hasn't caught up with it yet. Drives the
 * palette's "Searching…"-vs-"No results." decision during the 250ms
 * debounce window — without this, typing a 2+ char query briefly showed
 * "No results." (keyed off the stale debounced value) before flipping to
 * real results once the debounce and the queries settled. Exported for unit
 * testing.
 */
export function isContentSearchPending(search: string, debouncedSearch: string): boolean {
  return search.trim().length >= MIN_CONTENT_QUERY_LENGTH && debouncedSearch !== search;
}

/**
 * Pure: whether the six Recent-activity sections should render (and their
 * query should fetch) — the dialog is open AND the LIVE search box is
 * genuinely empty. Deliberately keyed off `search`, never the debounced
 * value or `MIN_CONTENT_QUERY_LENGTH`: with one character typed, or during
 * the ~250ms window before the debounce catches up to a longer query, the
 * debounced query is still below the content-search minimum while `search`
 * itself is not empty — gating on the debounced side (fix round 1,
 * FRONT-2391) left recents on screen, and `firstPaletteValue` returning a
 * recent row, for that whole window, so Enter right after the first
 * keystroke opened a recent item instead of doing nothing. Exported for
 * unit testing.
 */
export function shouldShowRecents(open: boolean, search: string): boolean {
  return open && search.trim() === "";
}

/**
 * Pure: whether a click on a palette row should be intercepted into a mere
 * "select" (true — the row's `onSelect`/open handler waits for Enter or a
 * double-click) or left alone to open the row directly on a single click
 * (false). FRONT-2391 product correction: while Recent activity is showing
 * (`showRecents`) there is no preview pane to look at first, so
 * select-then-open would leave a click doing nothing visible — the click
 * must open immediately. The moment real search results are on screen
 * (`showRecents` false), the palette's ordinary click-selects/
 * double-click-opens separation (FRONT-874) is back.
 *
 * `handleListClickCapture` is the actual interception, and it is a local
 * closure with no DOM to drive under this suite's plain-Node harness (no
 * jsdom — a `click` event can't be dispatched or observed here), so this
 * predicate is exported to make the WIRING DECISION itself unit-testable;
 * the full click-to-open flow is proven in
 * `tests/e2e/specs/search/search-recent-activity.spec.ts`.
 */
export function shouldInterceptRowClick(showRecents: boolean): boolean {
  return !showRecents;
}

/**
 * Pure: which row the palette pre-selects, so Enter always opens the row the
 * user is looking at — the first one rendered (FRONT-872).
 *
 * Takes a NAMED object rather than positional arguments on purpose. The hit
 * arrays for different sections have IDENTICAL types (`Array<{ id: string }>`
 * for both Work containers and Memory entities), so a call site that
 * transposed two of them would compile, pass every test, and quietly open the
 * wrong row on Enter — exactly the bug this helper exists to prevent.
 *
 * EVERY section is REQUIRED, deliberately. Work (FRONT-1256) and Memory
 * (FRONT-876) were built on separate branches and merged here; while these two
 * were optional, a take-one-side merge that dropped a section from this one
 * call site still compiled, still rendered both sections, and still opened both
 * on click — it only showed up as the highlight landing on whichever section's
 * query resolved last, which no browser test can pin down deterministically.
 * Required fields turn that into a compile error instead. A build that genuinely
 * lacks a section passes an empty array.
 *
 * The check order must match the render order of the sections below.
 * Exported for unit testing.
 */
export function firstPaletteValue(sections: {
  staticValues: string[];
  // FRONT-2391. Optional for the same reason appHits/deckHits/sketchHits
  // below are — existing callers/tests keep compiling — but the palette
  // itself always passes it: the Recent-activity sections render ABOVE
  // Navigate whenever they're showing at all, so a recent row (when one
  // exists) is the row that visually renders FIRST and must win the
  // tie-break over every static value, not just fall in after them.
  recentValues?: string[];
  workHits: Array<{ id: string }>;
  memoryHits: Array<{ id: string }>;
  chatHits: Array<{ sessionId: string }>;
  fileHits: Array<{ id: string }>;
  documentHits: Array<{ filename: string }>;
  // FRONT-1974. Optional so existing callers and tests keep compiling, but the
  // palette always passes them: a query matching ONLY a deck used to return
  // null here, which left nothing highlighted and made Enter do nothing at all.
  appHits?: Array<{ slug: string }>;
  deckHits?: Array<{ slug: string }>;
  sketchHits?: Array<{ slug: string }>;
  // FRONT-2436. Row values of the build sections in render order: knowledge
  // bases render between Files and Apps, the four Build kinds after Sketches.
  // Optional for the same reason as the apps kinds above.
  buildBeforeAppsValues?: string[];
  buildAfterAppsValues?: string[];
}): string | null {
  const { staticValues, workHits, memoryHits, chatHits, fileHits, documentHits } = sections;
  const recentValues = sections.recentValues ?? [];
  const appHits = sections.appHits ?? [];
  const deckHits = sections.deckHits ?? [];
  const sketchHits = sections.sketchHits ?? [];
  const buildBeforeAppsValues = sections.buildBeforeAppsValues ?? [];
  const buildAfterAppsValues = sections.buildAfterAppsValues ?? [];
  if (recentValues.length > 0) return recentValues[0];
  if (staticValues.length > 0) return staticValues[0];
  if (workHits.length > 0) return `work-${workHits[0].id}`;
  if (memoryHits.length > 0) return `memory-${memoryHits[0].id}`;
  if (chatHits.length > 0) return `chat-${chatHits[0].sessionId}`;
  if (fileHits.length > 0) return `file-${fileHits[0].id}`;
  if (documentHits.length > 0) return `doc-${documentHits[0].filename}`;
  if (buildBeforeAppsValues.length > 0) return buildBeforeAppsValues[0];
  // Render order: Apps, Slides and Sketches sit after Files in the JSX below,
  // so they come last here too, in that order. This function's whole job is to
  // name the row that renders FIRST, so the two orders have to stay in step.
  if (appHits.length > 0) return `app-${appHits[0].slug}`;
  if (deckHits.length > 0) return `deck-${deckHits[0].slug}`;
  if (sketchHits.length > 0) return `sketch-${sketchHits[0].slug}`;
  if (buildAfterAppsValues.length > 0) return buildAfterAppsValues[0];
  return null;
}

/**
 * Pure: the entity-kind label for a Memory search hit's row subtitle /
 * preview pill — delegates to the shared `entityTypeLabel` (`~/lib/entity-types`,
 * FRONT-876 review fix), which Title-Cases anything outside the graph's
 * curated kinds rather than echoing the raw lowercase type string. Exported
 * for unit testing.
 */
export function memoryEntityKindLabel(type: string): string {
  return entityTypeLabel(type);
}

/**
 * Pure: the bare fragment of what a Memory search hit's `matchedIn` means —
 * `searchEntities` (`GET /api/graph/search`) tags every hit with which of its
 * three passes found it: the entity's own name, a retired alias, or a saved
 * attribute value. A hit that matched on an alias or an attribute doesn't
 * necessarily contain the typed words anywhere visible in its name, so the
 * row/preview must say what actually matched — otherwise searching "CTO"
 * surfacing "Jane Doe" reads as an unexplained, random result. Returns null
 * for a name match (or an unrecognized value): the row's own highlighted
 * title already makes that case self-evident.
 *
 * FRONT-876 review fix: returns the bare fragment ("an alias", "a saved
 * detail") rather than a full sentence — callers prepend their own prefix
 * ("matched " for the row subtitle, "Matched: " for the preview pill) instead
 * of string-surgering a value this function already committed to a specific
 * shape (`note.replace("matched ", "")`, two functions away from where the
 * "matched " was added). Exported for unit testing.
 */
export function memoryMatchNote(matchedIn: string | undefined): string | null {
  if (matchedIn === "alias") return "an alias";
  if (matchedIn === "attribute") return "a saved detail";
  return null;
}

/**
 * Pure: the Memory row's subtitle — the entity's kind, plus (per
 * `memoryMatchNote`) what the hit matched in when that wasn't the name.
 * Exported for unit testing.
 */
export function memoryHitSubtitle(hit: { type: string; matchedIn?: string }): string {
  const kind = memoryEntityKindLabel(hit.type);
  const note = memoryMatchNote(hit.matchedIn);
  return note ? `${kind} · matched ${note}` : kind;
}

/**
 * Pure: the preview pane's match-kind pill label — "Matched by name" only
 * when the hit genuinely says so (`matchedIn === "name"`), "Matched: <note>"
 * for an alias/attribute match, or null to omit the pill entirely for
 * anything unrecognized or absent.
 *
 * FRONT-876 review fix: the previous version rendered "Matched by name"
 * whenever `memoryMatchNote` had nothing to say — which is ALSO true for an
 * unrecognized or missing `matchedIn`, not just a genuine name match. That
 * turned "we don't know what matched" into an asserted, specific claim. A
 * fourth match kind added to `searchEntities` (or a response that simply
 * omits the field) would have kept asserting "Matched by name" for a hit
 * that didn't. Exported for unit testing.
 */
export function memoryMatchPillLabel(matchedIn: string | undefined): string | null {
  if (matchedIn === "name") return "Matched by name";
  const note = memoryMatchNote(matchedIn);
  return note ? `Matched: ${note}` : null;
}

/**
 * Pure: mirrors the backend's `isSetAside` (`src/graph/archive-reason.ts`) —
 * true only for a search hit a PERSON set aside (`archive_reason === "user"`),
 * never one that merely decayed out on its own. `searchEntities` (the query
 * behind both `useWorkContainerSearch` and `useMemoryEntitySearch`) already
 * excludes decay-faded rows via
 * `memoryVisibleWhere()`, so in practice every archived hit reaching the
 * palette is already user-set-aside — this checks both fields anyway rather
 * than assuming that invariant holds forever, same discipline the backend
 * check itself uses. Exported for unit testing.
 */
export function isEntityHitInactive(hit: {
  archived_at?: string | null;
  archive_reason?: string | null;
}): boolean {
  return !!hit.archived_at && hit.archive_reason === "user";
}

/**
 * Pure: the display name for a Work or Memory row/preview — mirrors the
 * backend's `withInactiveMarker` (`src/graph/archive-reason.ts`), same
 * "(Inactive)" suffix and same wording, so a finished project or a set-aside
 * person can never read as a live one here either (FRONT-1235's rule, applied
 * to the command palette). Kept as a client-side twin rather than a shared
 * import: the frontend can't import backend modules across the build
 * boundary. Exported for unit testing.
 */
export function entityHitDisplayName(hit: {
  name: string;
  archived_at?: string | null;
  archive_reason?: string | null;
}): string {
  return isEntityHitInactive(hit) ? `${hit.name} (Inactive)` : hit.name;
}

/**
 * Pure: the Memory page's entity deep-link hash — built from the SAME
 * `buildHash` the Memory page itself uses (`~/lib/memory-hash`), so the two
 * can never drift apart the way this used to (FRONT-876 review fix: this was
 * a hand-copied twin of `buildHash`'s Entities-tab format, byte-correct at
 * the time but pinned by nothing — a change to `buildHash` wouldn't have
 * turned a single test red here). `&view=archived` is appended for a
 * set-aside entity, since an archived row is only visible in that page's
 * Inactive view — see `isEntityHitInactive`. Exported for unit testing.
 */
export function memoryEntityHref(hit: {
  id: string;
  archived_at?: string | null;
  archive_reason?: string | null;
}): string {
  const view: EntityView = isEntityHitInactive(hit) ? "archived" : "active";
  return `/memory${buildHash("entities", hit.id, view)}`;
}

/**
 * Pure: the "Open text" navigation target for a file search hit (FRONT-1642)
 * — the Files detail route, with the search term (when non-empty) riding
 * along as `?q=` so `FileDetail` (`~/routes/files.tsx`) can highlight the
 * passage that matched. No query ⇒ the bare path, byte-identical to every
 * navigation before this ticket. Shared by the row's own Enter/double-click
 * `onSelect` and `actionForValue`'s "file-" branch (the preview pane's
 * button) so the two can never build two different paths for the same hit.
 *
 * Deliberately dumb — it always appends a non-empty query. BOTH call sites
 * are responsible for passing `""` instead of the live search term unless
 * `shouldSplitFileActions(hit.kind, hit.hasBytes)` is true (review fix:
 * every file kind was passing the term, so opening a plain text/markdown/
 * binary hit from search also carried `?q=` and turned on the HTML-rewriting
 * highlighter — a hit only HAS a highlightable passage when it's the
 * PDF-today/image-later case with both bytes and extracted text). This
 * function has no access to `kind`/`hasBytes` on its own, so it can't
 * enforce that gate itself — see the two call sites for the actual check.
 * Exported for unit testing.
 */
export function fileDetailPath(fileId: string, query: string): string {
  const trimmed = query.trim();
  return trimmed ? `/files/${fileId}?q=${encodeURIComponent(trimmed)}` : `/files/${fileId}`;
}

/**
 * Short one-line descriptions for the "Page" preview pane (FRONT-874). A
 * small local map — deliberately NOT stored on `sidebar-nav-catalog.ts`,
 * which owns navigation, not preview copy. Covers every catalog destination
 * plus the hand-added Navigate/Account entries below.
 */
const PAGE_DESCRIPTIONS: Record<string, string> = {
  inbox: "Your daily briefing and everything that needs a look.",
  chats: "Every conversation you've had with EDDY, searchable.",
  memory: "People, companies, projects and facts EDDY remembers about your world.",
  files: "Everything you've uploaded, plus documents EDDY has saved for you.",
  apps: "Mini-apps you've built or published with EDDY.",
  policy: "Autonomy rules — what EDDY may do without asking first.",
  agents: "Custom agents you've configured for specialized work.",
  schedule: "Recurring jobs EDDY runs on a schedule.",
  skills: "Reusable multi-step playbooks EDDY can run.",
  tools: "Custom tools you've wired up for EDDY to call.",
  connections: "Connect providers and MCP servers to EDDY.",
  audit: "A record of every consequential action EDDY has taken.",
  "org-settings": "Organization-wide configuration and members.",
  sandbox: "The sandboxed environment EDDY's code runs in.",
  status: "Live health of EDDY's runtime and connected services.",
  diagnostics: "Deep technical inspection for troubleshooting.",
  chat: "Start a fresh conversation with EDDY.",
  notifications: "Alerts and updates EDDY has sent you.",
  account: "You, EDDY, your models, your usage, and your sessions.",
  "sign-out": "End your session on this device.",
};

/** Icon + label for the hand-added Navigate/Account rows that aren't sidebar
 *  destinations (kept alongside the catalog-derived ones, same as before
 *  FRONT-874 — just needed here too now for the preview pane). */
const EXTRA_PAGE_ITEMS: Record<string, { label: string; icon: Icon }> = {
  chat: { label: "Chat", icon: ChatCircleIcon },
  notifications: { label: "Notifications", icon: BellIcon },
  account: { label: "Account", icon: GearIcon },
  "sign-out": { label: "Sign out", icon: SignOutIcon },
};

function pageMeta(id: string): { label: string; icon: Icon } | null {
  const dest = getSidebarDestination(id);
  if (dest) return { label: dest.label, icon: dest.icon };
  // FRONT-2376: the eight System Settings sections aren't sidebar
  // destinations any more — a small linear find over 8 entries, not worth a
  // Map for a lookup this rare (only fired when the preview pane opens).
  const sys = SYSTEM_SETTINGS_SECTIONS.find((s) => s.id === id);
  if (sys) return { label: sys.label, icon: sys.icon };
  return EXTRA_PAGE_ITEMS[id] ?? null;
}

/** Compact "6 Jul" style date for row meta + preview eyebrows. */
function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return formatDate(d, { day: "numeric", month: "short" });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Presentational building blocks — the "Explorer" visual language (FRONT-874):
// the zen-modal surface (see inbox-chat-modal.tsx's backdrop/card)
// with a sectioned result list on the left and a live preview pane on the
// right. Kept local to this file (none of these are needed elsewhere yet).
// ---------------------------------------------------------------------------

/** The EDDY mark used in the header — the small "E" tile the zen chat modal
 *  used before FRONT-2379 moved it to the shared wordmark. */
function PaletteMark() {
  return (
    <span className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-md bg-foreground text-xs font-semibold text-background">
      E
    </span>
  );
}

function FooterHint({
  keys,
  label,
  srLabel,
  className,
}: {
  keys: string;
  label: string;
  /** Spoken form, for hints whose `keys` are punctuation a screen reader
   *  would read character by character (e.g. `"…"` → "quote ellipsis quote"). */
  srLabel?: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground ${className ?? ""}`}
      {...(srLabel ? { role: "note", "aria-label": srLabel } : {})}
    >
      <kbd
        aria-hidden={srLabel ? true : undefined}
        className="rounded-md border border-border bg-card px-1.5 py-0.5 font-mono text-[length:var(--text-2xs)] text-muted-foreground"
      >
        {keys}
      </kbd>
      <span aria-hidden={srLabel ? true : undefined}>{label}</span>
    </span>
  );
}

/**
 * FRONT-1642: the file preview's "Open file" affordance for a hit that has
 * BOTH original bytes and extracted text (see `shouldSplitFileActions`) — a
 * real anchor to the raw-bytes route (`rawFileUrl`,
 * `~/features/chat/modal-asset`), opening the actual file in a new tab.
 * Rendered ONLY alongside "Open text" (`OpenAffordance` above) in that one
 * case — never as a hit's sole action — so it never carries the ↵ badge
 * (Enter always fires "Open text" there) and never needs its own top
 * margin (the wrapping row in `FilePreview` supplies one for the pair).
 * Styled identically to `OpenAffordance` via the shared
 * `OPEN_AFFORDANCE_CLASS` so the two sit side by side as one visual family.
 */
function OpenFileLinkAffordance({ href }: { href: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={OPEN_AFFORDANCE_CLASS}>
      Open file
    </a>
  );
}

/** The matched-passage card: border + card background + generous padding.
 *  `faded` adds the bottom gradient that signals a truncated peek. */
function ExcerptCard({ children, faded }: { children: ReactNode; faded?: boolean }) {
  return (
    <div className="relative mt-5 rounded-xl border border-border bg-card p-[18px] text-sm leading-relaxed text-foreground">
      {children}
      {faded && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-xl bg-gradient-to-t from-card to-transparent" />
      )}
    </div>
  );
}

/** Renders a search snippet's `<b>` highlights as real React elements — never
 *  `dangerouslySetInnerHTML`. Each segment's text is run through
 *  `cleanSnippetText` (FRONT-874 fidelity fix) so the stray markdown syntax
 *  `ts_headline` leaves in a chat snippet doesn't render literally — cleaning
 *  per-segment (rather than on the raw snippet before parsing) keeps the
 *  `<b>` highlight boundaries intact. Used for both the chat preview pane's
 *  excerpt and the chat list rows' snippet line. */
function HighlightedSnippet({ snippet }: { snippet: string }) {
  return (
    <>
      {parseSnippet(snippet).map((seg, i) => {
        const text = cleanSnippetText(seg.text);
        return seg.highlighted ? (
          <strong key={i} className="font-semibold text-primary">
            {text}
          </strong>
        ) : (
          <span key={i}>{text}</span>
        );
      })}
    </>
  );
}

function PreviewSkeleton() {
  return (
    <div className="mt-5 space-y-2">
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-5/6" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  );
}

function PagePreview({ id, onOpen }: { id: string; onOpen?: () => void }) {
  const meta = pageMeta(id);
  if (!meta) return <EmptyPreview />;
  const PageIcon = meta.icon;
  const description = PAGE_DESCRIPTIONS[id];
  return (
    <div>
      <div className="grid size-14 place-items-center rounded-2xl border border-border bg-card text-foreground">
        <PageIcon size={26} weight="regular" />
      </div>
      <PreviewTitle>{meta.label}</PreviewTitle>
      {description && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>}
      <OpenAffordance label="Go to page" onOpen={onOpen} />
    </div>
  );
}

/**
 * FRONT-1256 review fix: the Work section's preview branch — a container has
 * no extra data to fetch (everything needed already rides on the search
 * hit), so this stays as simple as `PagePreview`: an eyebrow, the name (with
 * the "(Inactive)" marker when the container was set aside — see
 * `entityHitDisplayName`), a kind pill (Project, matching the row's own
 * `snippet`), and the open affordance. FRONT-2378 retired `routine` as a Work
 * container kind, so there is only ever one kind to show here now.
 */
function WorkPreview({ hit, onOpen }: { hit: GraphEntity; onOpen?: () => void }) {
  return (
    <div>
      <PreviewEyebrow label="Projects" />
      <PreviewTitle>{entityHitDisplayName(hit)}</PreviewTitle>
      <PreviewByline>
        <PalettePill>Project</PalettePill>
      </PreviewByline>
      <OpenAffordance label="Open project" onOpen={onOpen} />
    </div>
  );
}

/**
 * The Memory section's preview branch — a knowledge-graph entity has no
 * extra data to fetch (everything needed already rides on the search hit),
 * so this stays as simple as `PagePreview`/`WorkPreview`: an eyebrow, the
 * name (with the "(Inactive)" marker when the entity was set aside — see
 * `entityHitDisplayName`), a kind pill, a pill spelling out what the hit
 * matched in, and the open affordance.
 */
function MemoryPreview({ hit, onOpen }: { hit: GraphEntitySearchHit; onOpen?: () => void }) {
  const matchPill = memoryMatchPillLabel(hit.matchedIn);
  return (
    <div>
      <PreviewEyebrow label="Memory" />
      <PreviewTitle>{entityHitDisplayName(hit)}</PreviewTitle>
      <PreviewByline>
        <PalettePill>{memoryEntityKindLabel(hit.type)}</PalettePill>
        {matchPill && <PalettePill>{matchPill}</PalettePill>}
      </PreviewByline>
      <OpenAffordance label="Open in Memory" onOpen={onOpen} />
    </div>
  );
}

/**
 * FRONT-2142 — the ONE predicate deciding where Enter goes for an apps-table
 * row, and therefore also whether the preview pane marks it as leaving the SPA.
 *
 * An app's owner lands on the app's edit page, in this tab. Everyone else —
 * any non-owner, and the owner of a deck or sketch, whose only editor is the
 * chat — lands on the served page at `/apps/<slug>/`, in a new tab.
 *
 * This mirrors the Apps page, where the split is deliberate rather than
 * incidental: `apps-admin.tsx` wraps its whole-card edit link in `isOwner`,
 * and gates `AppPreview`'s own click-through separately — missing that second
 * affordance was a FRONT-2029 review finding. The palette must not offer a
 * non-owner a route that page denies them.
 *
 * It is ONE function rather than a condition written at each site because the
 * destination and the "opens in a new tab" marker are one decision: two
 * expressions of the same rule drift, and the drift here would be a preview
 * that lies about what the row is going to do (the FRONT-1670 class).
 */
export function opensEditPage(hit: Pick<AppSearchHit, "kind" | "isOwner" | "isEditor">): boolean {
  // FRONT-2937: the app's editors reach its Edit page too.
  return hit.kind === "app" && (hit.isOwner || hit.isEditor === true);
}

/** Section headings, and the eyebrow the preview pane shows. "Slides" rather
 *  than "Decks" is the product's own name for the surface (FRONT-1974). */
const APP_KIND_SECTION_LABEL: Record<AppSearchHit["kind"], string> = {
  app: "Apps",
  deck: "Slides",
  sketch: "Sketches",
};

const APP_KIND_OPEN_LABEL: Record<AppSearchHit["kind"], string> = {
  app: "Open app",
  deck: "Open deck",
  sketch: "Open sketch",
};

/** Render order for the apps-table sections. `firstPaletteValue` walks the
 *  same order — the two must stay in step, since that function's only job is
 *  to name the row that renders first. */
const APPS_SECTION_ORDER = ["app", "deck", "sketch"] as const;

const APP_KIND_ICON = {
  // The globe, not a window — this is the SAME icon the sidebar's Apps entry
  // uses (`sidebar-nav-catalog.ts`) and the same one the Apps page falls back
  // to on a card with no preview. The palette renders that nav entry as its own
  // "Apps" page row, so a different glyph here would put two icons for Apps in
  // one dialog.
  app: GlobeIcon,
  deck: PresentationIcon,
  sketch: PencilSimpleIcon,
} as const;

/**
 * FRONT-2391 — one row renderer for the three apps-table Recent sections
 * (Recent apps / Recent sketches / Recent slides), same reasoning
 * `APPS_SECTION_ORDER`'s shared renderer above gives for the search-hit
 * version: three near-identical blocks is how two of them silently drift.
 * `hit.kind` (every row in `hits` shares one, by construction of the wire —
 * `src/recents/service.ts` buckets by `apps.kind`) picks the icon and the
 * `recent-<kind>-<slug>` row value; `openRow` is the palette's own
 * `openAppsRow`, so a recent app/sketch/deck opens exactly the way the same
 * row does from a content search.
 *
 * FRONT-2391 product correction (fix round 3): icon + title only, one line —
 * no description snippet, no date meta. The product owner's own words:
 * "the results should be simple one-liners under the search ... One column
 * is more than enough."
 */
export function RecentAppsSection({
  hits,
  testId,
  heading,
  openRow,
}: {
  hits: RecentAppHit[];
  testId: string;
  heading: string;
  openRow: (hit: AppSearchHit) => () => void;
}) {
  if (hits.length === 0) return null;
  return (
    <PaletteSection label={heading} count={hits.length} testId={testId}>
      {hits.map((hit) => (
        <PaletteRow
          key={hit.slug}
          testId={`${testId}-row`}
          value={`recent-${hit.kind}-${hit.slug}`}
          icon={APP_KIND_ICON[hit.kind]}
          title={hit.title}
          onSelect={openRow(hit)}
        />
      ))}
    </PaletteSection>
  );
}

/** FRONT-2391 — "Recent chats", one line per row (product correction, fix
 *  round 3): the title, then the first-message preview folded onto the SAME
 *  line in muted text via `recentChatRowTitle` — never a second, wrapped
 *  block, never `dangerouslySetInnerHTML`. `openRow` is the palette's own
 *  `openChat`. */
export function RecentChatsSection({
  hits,
  openRow,
}: {
  hits: RecentChatHit[];
  openRow: (hit: RecentChatHit) => () => void;
}) {
  if (hits.length === 0) return null;
  const testId = RECENT_SECTION_TEST_ID.chats;
  return (
    <PaletteSection label={RECENT_SECTION_HEADING.chats} count={hits.length} testId={testId}>
      {hits.map((hit) => (
        <PaletteRow
          key={hit.sessionId}
          testId={`${testId}-row`}
          value={`recent-chat-${hit.sessionId}`}
          icon={ChatCircleIcon}
          title={recentChatRowTitle(hit)}
          onSelect={openRow(hit)}
        />
      ))}
    </PaletteSection>
  );
}

/** FRONT-2391 — "Recent projects", icon + name only, one line (product
 *  correction, fix round 3 — the icon already says "project", so the old
 *  "Project" snippet line was redundant). `openRow` is the palette's own
 *  `go(`/work/${hit.id}`)`. */
export function RecentProjectsSection({
  hits,
  openRow,
}: {
  hits: RecentProjectHit[];
  openRow: (hit: RecentProjectHit) => () => void;
}) {
  if (hits.length === 0) return null;
  const testId = RECENT_SECTION_TEST_ID.projects;
  return (
    <PaletteSection label={RECENT_SECTION_HEADING.projects} count={hits.length} testId={testId}>
      {hits.map((hit) => (
        <PaletteRow
          key={hit.id}
          testId={`${testId}-row`}
          value={`recent-project-${hit.id}`}
          icon={BriefcaseIcon}
          title={hit.name}
          onSelect={openRow(hit)}
        />
      ))}
    </PaletteSection>
  );
}

/** FRONT-2391 — "Recent knowledge": files and documents, bucketed by
 *  `hit.kind` alone (same array, both kinds — the wire never splits them).
 *  `openRow` dispatches file vs document itself (a file goes through
 *  `fileDetailPath`, a document through `openDocument` via
 *  `recentKnowledgeToDocumentMeta`) — kept in the caller rather than here so
 *  this component stays a pure renderer with no `DocumentMeta` construction
 *  of its own. Icon + title only, one line (product correction, fix round
 *  3) — the date meta this row used to carry is dropped. */
export function RecentKnowledgeSection({
  hits,
  openRow,
}: {
  hits: RecentKnowledgeHit[];
  openRow: (hit: RecentKnowledgeHit) => () => void;
}) {
  if (hits.length === 0) return null;
  const testId = RECENT_SECTION_TEST_ID.knowledge;
  return (
    <PaletteSection label={RECENT_SECTION_HEADING.knowledge} count={hits.length} testId={testId}>
      {hits.map((hit) => (
        <PaletteRow
          key={`${hit.kind}-${hit.id}`}
          testId={`${testId}-row`}
          value={`recent-${hit.kind}-${hit.id}`}
          icon={FilesIcon}
          title={hit.title}
          onSelect={openRow(hit)}
        />
      ))}
    </PaletteSection>
  );
}

/**
 * FRONT-1974, widened to apps by FRONT-2142 — the preview for anything that
 * lives in the `apps` table: an app, a slide deck or a sketch.
 *
 * Metadata only: title, description, audience and the updated date, all of
 * which are already on the wire from `/api/apps-search`, so this needs no
 * detail fetch of its own. No thumbnail — nothing renders one today, and a
 * scaled live iframe would load a whole bundle every time the highlight moved.
 *
 * The external-link glyph beside the action is the ONLY signal that Enter
 * leaves the app, and it is shown ONLY when that is actually true. An earlier
 * draft explained it in a bordered callout; that was cut as over-signalling —
 * the wiki's cognitive-load rule (remove what does not help the reader act)
 * plus its similarity/emphasis pair (a genuinely different behaviour earns a
 * PROPORTIONAL marker, not a structurally different block in an otherwise
 * uniform pane). The glyph is decorative to assistive tech, so the same fact
 * is carried in text for screen readers rather than in the icon alone.
 *
 * FRONT-2142: an app's OWNER is the one case that does not leave the SPA —
 * they go to the app's edit page — so for them the glyph and its screen-reader
 * twin are both absent. A marker for a jump that is not about to happen is
 * worse than none: it is a false statement about what Enter will do.
 */
function ServedAppPreview({
  hit,
  onOpen,
}: {
  hit: AppSearchHit;
  onOpen?: () => void;
}) {
  const date = formatShortDate(hit.updatedAt);
  // `people` is REDACTED to null for anyone who is not the owner
  // (`search-routes.ts`: `people: isOwner ? activeShareEmails(row) : null`), so
  // null means UNKNOWN, never "nobody". Reading it as nobody labelled a
  // colleague's named-shared deck "Only you" — the same wrong-audience bug
  // FRONT-1880 fixed once already (review panel finding, FRONT-1974).
  // A non-owner is told the one thing that is certainly true: it reached them.
  let audience = "Only you";
  if (hit.workspaceShared) {
    audience = "Shared with workspace";
  } else if (!hit.isOwner) {
    audience = "Shared with you";
  } else if ((hit.people?.length ?? 0) > 0) {
    const recipients = hit.people?.length ?? 0;
    audience = `Shared with ${recipients} ${recipients === 1 ? "person" : "people"}`;
  }
  const leavesTheApp = !opensEditPage(hit);
  return (
    <div>
      <PreviewEyebrow label={APP_KIND_SECTION_LABEL[hit.kind]} date={date} />
      <PreviewTitle>{hit.title}</PreviewTitle>
      <PreviewByline date={date}>
        <PalettePill>{audience}</PalettePill>
      </PreviewByline>
      {hit.description && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{hit.description}</p>
      )}
      <div className="flex items-center gap-2">
        <OpenAffordance label={APP_KIND_OPEN_LABEL[hit.kind]} onOpen={onOpen} />
        {leavesTheApp && (
          <>
            <ArrowSquareOutIcon size={13} weight="bold" className="mt-[22px] text-primary" aria-hidden="true" />
            <span className="sr-only">opens in a new tab</span>
          </>
        )}
      </div>
    </div>
  );
}

function ChatPreview({
  hit,
  onOpen,
}: {
  hit: ChatSearchHit;
  onOpen?: () => void;
}) {
  const date = formatShortDate(hit.lastActiveAt ?? hit.updatedAt ?? hit.createdAt);
  const attachments = hit.attachmentNames ?? [];
  return (
    <div>
      <PreviewEyebrow label="Chat" date={date} />
      <PreviewTitle>{hit.title || "Untitled chat"}</PreviewTitle>
      <PreviewByline date={date}>
        <PalettePill>
          {hit.messageCount} {hit.messageCount === 1 ? "message" : "messages"}
        </PalettePill>
        {attachments.map((name) => (
          <PalettePill key={name}>{name}</PalettePill>
        ))}
      </PreviewByline>
      {hit.snippet ? (
        <ExcerptCard><HighlightedSnippet snippet={hit.snippet} /></ExcerptCard>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">No preview available.</p>
      )}
      <OpenAffordance label="Open chat" onOpen={onOpen} />
    </div>
  );
}

/**
 * Memory documents are agent-written markdown; the preview peek renders them
 * as plain text, so bare `###`/`**` markers read as clutter. Strips only the
 * unambiguous markdown syntax (headings, strong, code spans, links, quotes) —
 * single `*`/`_` emphasis is left alone since those characters legitimately
 * appear in prose and identifiers. Deliberately NOT applied to file peeks:
 * uploaded files are arbitrary formats (a `#` in Python is a comment, not a
 * heading) and their raw content is the honest preview.
 */
export function stripMarkdownPeek(s: string): string {
  return s
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/`{1,3}([^`]*)`{1,3}/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^>\s?/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function DocumentPreview({
  hit,
  content,
  loading,
  onOpen,
}: {
  hit: DocumentMeta;
  content: string | undefined;
  loading: boolean;
  onOpen?: () => void;
}) {
  const date = formatShortDate(hit.updatedAt || hit.date);
  // Strip over a slightly larger window than the peek so markup removed near
  // the boundary doesn't leave the visible excerpt short.
  const peek = content
    ? stripMarkdownPeek(content.slice(0, PREVIEW_CONTENT_PEEK_CHARS * 3)).slice(0, PREVIEW_CONTENT_PEEK_CHARS)
    : undefined;
  return (
    <div>
      <PreviewEyebrow label="Memory document" date={date} />
      <PreviewTitle>{hit.title || hit.filename}</PreviewTitle>
      <PreviewByline date={date}>
        {hit.tags.map((tag) => (
          <PalettePill key={tag}>{tag}</PalettePill>
        ))}
      </PreviewByline>
      {hit.summary && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{stripMarkdownPeek(hit.summary)}</p>
      )}
      {hit.snippet ? (
        // Content matched the search — show the ts_headline excerpt with the
        // matched terms highlighted (same treatment as ChatPreview) instead of
        // the generic start-of-document peek. Renders instantly: the snippet
        // rides on the search hit, no detail fetch needed.
        <ExcerptCard>
          <HighlightedSnippet snippet={hit.snippet} />
        </ExcerptCard>
      ) : loading ? (
        <PreviewSkeleton />
      ) : peek ? (
        <ExcerptCard faded={(content?.length ?? 0) > PREVIEW_CONTENT_PEEK_CHARS}>{peek}</ExcerptCard>
      ) : null}
      <OpenAffordance label="Open document" onOpen={onOpen} />
    </div>
  );
}

/** Exported for a render-level test (FRONT-1642 review fix — see
 *  `command-palette.test.tsx`'s "FilePreview — rendered actions" describe
 *  block): the pure `shouldSplitFileActions` boolean was covered, but
 *  nothing asserted what actually renders, which is exactly how the
 *  narrowing this ticket was sent back for once (an image/binary hit's
 *  single action silently turning into a raw-bytes anchor) could regress
 *  again without a single test going red. */
export function FilePreview({
  hit,
  content,
  loading,
  onOpen,
}: {
  hit: FileMeta;
  content: string | undefined;
  loading: boolean;
  onOpen?: () => void;
}) {
  const date = formatShortDate(hit.updatedAt);
  const isTextual = uploadHasTextBody(hit.kind);
  const peek = content ? content.slice(0, PREVIEW_CONTENT_PEEK_CHARS) : undefined;
  // FRONT-1642: a PDF has TWO distinct things behind one row — the
  // original file and its extracted text — so ONLY that combination splits
  // into two actions. Every other kind (a plain text/markdown hit, or an
  // image/binary hit) keeps the single "Open file" action exactly as it
  // always has been. FRONT-1607 (image OCR) does not change this on its
  // own — see `shouldSplitFileActions`'s own doc comment (`palette-file-
  // actions.ts`) for why an image hit needs a second, separate change
  // before it can ever split too.
  const splitActions = shouldSplitFileActions(hit.kind, hit.hasBytes);
  return (
    <div>
      <PreviewEyebrow label="File" date={date} />
      <PreviewTitle>{hit.name}</PreviewTitle>
      <PreviewByline date={date}>
        <PalettePill>{hit.contentType || "Unknown type"}</PalettePill>
        <PalettePill>{formatBytes(hit.sizeBytes)}</PalettePill>
      </PreviewByline>
      {hit.description && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{hit.description}</p>
      )}
      {hit.snippet ? (
        // Content matched the search — show the ts_headline excerpt with the
        // matched terms highlighted (same treatment as ChatPreview) instead of
        // the generic start-of-file peek. Binary files never carry a snippet
        // (their extracted content is empty), so this branch can't shadow the
        // binary notice below.
        <ExcerptCard>
          <HighlightedSnippet snippet={hit.snippet} />
        </ExcerptCard>
      ) : !isTextual ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
          <FilesIcon size={16} /> Binary file — no preview
        </div>
      ) : loading ? (
        <PreviewSkeleton />
      ) : peek ? (
        <ExcerptCard faded={(content?.length ?? 0) > PREVIEW_CONTENT_PEEK_CHARS}>{peek}</ExcerptCard>
      ) : null}
      {/* Enter always navigates to the file page (see `actionForValue`'s
       * "file-" branch, unchanged) — so when both actions show, the ↵ badge
       * sits on "Open text" (the Enter-synced one) and never on the anchor.
       * Every other kind renders the single "Open file" action exactly as
       * it did before FRONT-1642: same button, same label, same badge. */}
      {splitActions ? (
        <div className="mt-[22px] flex flex-wrap items-center gap-5">
          <OpenFileLinkAffordance href={rawFileUrl(hit.id)} />
          <OpenAffordance label="Open text" onOpen={onOpen} withMargin={false} />
        </div>
      ) : (
        <OpenAffordance label="Open file" onOpen={onOpen} />
      )}
    </div>
  );
}

/**
 * Dispatches the live preview pane off the currently-selected cmdk value
 * (the same prefixed `page-`/`work-`/`memory-`/`chat-`/`file-`/`doc-` strings
 * the rows render with). Work, Memory and chat metadata are already in hand
 * from the search hit itself (no extra fetch); file/document previews
 * additionally pull their content lazily via `fileDetail`/`documentDetail`
 * (see the debounce + viewport gates in `CommandPalette` — those hooks are
 * only *enabled* once the selection has settled for
 * `PREVIEW_DETAIL_DEBOUNCE_MS` AND the pane is actually visible at `lg`).
 * `fileDetail`/`documentDetail`'s `isLoading` here already folds in "still
 * arming" so the skeleton shows immediately, not just once the query itself
 * starts fetching.
 */
function PalettePreviewPane({
  selected,
  workHits,
  memoryHits,
  chatHits,
  fileHits,
  documentHits,
  appHits,
  deckHits,
  sketchHits,
  buildHits,
  fileDetail,
  documentDetail,
  onOpen,
}: {
  selected: string;
  workHits: GraphEntity[];
  memoryHits: GraphEntitySearchHit[];
  chatHits: ChatSearchHit[];
  fileHits: FileMeta[];
  documentHits: DocumentMeta[];
  appHits: AppSearchHit[];
  deckHits: AppSearchHit[];
  sketchHits: AppSearchHit[];
  buildHits: Record<BuildKind, BuildItem[]>;
  fileDetail: { data: { content: string } | undefined; isLoading: boolean };
  documentDetail: { data: { content: string } | undefined; isLoading: boolean };
  onOpen?: () => void;
}) {
  if (!selected) return <EmptyPreview />;
  if (selected.startsWith("page-")) return <PagePreview id={selected.slice("page-".length)} onOpen={onOpen} />;
  if (isBuildItemValue(selected)) {
    const item = findBuildItem(buildHits, selected);
    return item ? <BuildItemPreview item={item} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("work-")) {
    const hit = workHits.find((w) => w.id === selected.slice("work-".length));
    return hit ? <WorkPreview hit={hit} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("memory-")) {
    const hit = memoryHits.find((h) => h.id === selected.slice("memory-".length));
    return hit ? <MemoryPreview hit={hit} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("app-")) {
    const hit = appHits.find((a) => a.slug === selected.slice("app-".length));
    return hit ? <ServedAppPreview hit={hit} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("deck-")) {
    const hit = deckHits.find((d) => d.slug === selected.slice("deck-".length));
    return hit ? <ServedAppPreview hit={hit} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("sketch-")) {
    const hit = sketchHits.find((d) => d.slug === selected.slice("sketch-".length));
    return hit ? <ServedAppPreview hit={hit} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("chat-")) {
    const hit = chatHits.find((h) => h.sessionId === selected.slice("chat-".length));
    return hit ? <ChatPreview hit={hit} onOpen={onOpen} /> : <EmptyPreview />;
  }
  if (selected.startsWith("file-")) {
    const hit = fileHits.find((f) => f.id === selected.slice("file-".length));
    if (!hit) return <EmptyPreview />;
    return (
      <FilePreview hit={hit} content={fileDetail.data?.content} loading={fileDetail.isLoading} onOpen={onOpen} />
    );
  }
  if (selected.startsWith("doc-")) {
    const hit = documentHits.find((d) => d.filename === selected.slice("doc-".length));
    if (!hit) return <EmptyPreview />;
    return (
      <DocumentPreview
        hit={hit}
        content={documentDetail.data?.content}
        loading={documentDetail.isLoading}
        onOpen={onOpen}
      />
    );
  }
  // FRONT-2391 product correction: this pane no longer dispatches `recent-*`
  // values at all — while Recent activity is showing (`showRecents`), the
  // pane isn't even in the DOM (see the one-column layout in
  // `CommandPalette` below), so a highlighted recent row has nothing to
  // preview. `selected` only ever carries a `recent-*` prefix during that
  // window, so this branch would be unreachable dead code if it existed.
  return <EmptyPreview />;
}

/**
 * Global command palette (⌘K / Ctrl-K). Surfaces navigation targets, panel
 * toggles, account actions, and — since FRONT-872 (extended to Work
 * containers by FRONT-1256 and to knowledge-graph entities by FRONT-876) —
 * real content (work containers, chats, files, memory documents,
 * people/companies/events/preferences) in one searchable place.
 *
 * FRONT-874: rebuilt as the "Explorer" layout — a zen-modal-style surface
 * (same backdrop/card recipe as the inbox chat modal) with the sectioned
 * result list on the left and a live preview pane on the right, replacing
 * the old small cmdk dropdown. The result list still relies on cmdk for
 * keyboard nav/selection (`Command`/`Item`/`List` from the `cmdk` package),
 * but the palette composes the dialog and the row/section markup directly
 * instead of going through `CommandDialog`/`CommandInput`/`CommandItem`/
 * `CommandGroup` — those shared wrappers' hardcoded shadcn styling (fixed
 * centering, `rounded-xl!`, an auto-inserted checkmark icon, InputGroup
 * chrome around the search box, …) fights this surface's bespoke zen sizing
 * at nearly every layer, and `CommandDialog` has no other consumers (checked
 * via a repo-wide grep) so there was nothing else to preserve by keeping it
 * in the loop. `CommandList` is the one shared piece still reused — per the
 * redesign spec its only needed change is a call-site className override
 * (`max-h-none h-full` in place of the default `max-h-72` cap), so there was
 * no reason to fork it too.
 *
 * FRONT-285: the "Navigate" group is derived from the sidebar's destination
 * catalog (`sidebar-nav-catalog.ts`) rather than a second hand-maintained
 * list. "Chat" (the legacy `/chat` alias), "Notifications", and the Account
 * rows aren't sidebar destinations, so they stay as hand-added entries
 * alongside the catalog-derived ones (see `EXTRA_PAGE_ITEMS` for their
 * preview-pane icon/label).
 *
 * FRONT-874 follow-up: the "Panels" group (Toggle Inspector / Toggle Dev
 * Console) was removed from the palette entirely — both panels stay
 * reachable elsewhere (the Inspector via its header icon button in
 * `app-header.tsx`; the Dev Console via its own global Cmd/Ctrl+J shortcut
 * in `dev-console.tsx`), so the palette no longer needs to expose them.
 *
 * FRONT-872: `shouldFilter` is off and the typed query is tracked ourselves —
 * static groups are filtered client-side (instantly, via
 * `matchesPaletteQuery`) while Work/Memory/Chats/Files/Documents are filtered
 * server-side behind a 250ms debounce (content search is a network call; the
 * static groups are not).
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Controlled cmdk selection (FRONT-872 follow-up) — see `firstPaletteValue`
  // and the pin effect below for why this can't be left to cmdk itself.
  const [selected, setSelected] = useState("");
  // Press-started-on-backdrop dismissal (FRONT-874) — the why lives in
  // use-backdrop-dismiss.
  const backdropDismiss = useBackdropDismiss(() => setOpen(false));
  const navigate = useNavigate();
  const location = useLocation();
  // FRONT-1938: the palette's own "Settings" entries go through the same
  // choke point the avatar menu does — see `use-open-settings.ts`.
  const openSettings = useOpenSettings();
  const openSystemSettings = useOpenSystemSettings();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useSuperAdmin();
  // FRONT-1501: same reason as `mission-control-sidebar.tsx` — the palette
  // must apply the same `flag`-gate `isSidebarDestinationVisible` applies to
  // the sidebar rail, or a flag-off destination stays findable here even
  // though it's hidden everywhere else.
  const { data: flagsData, isLoading: flagsLoading, isError: flagsError } = useFeatureFlags();
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  const requestOpenDocument = useChatOpenStore((s) => s.requestOpenDocument);
  const requestOpenCoding = useCodingOpenStore((s) => s.requestOpenCoding);
  const sidebarNavDestinations: PaletteNavDestination[] = SIDEBAR_DESTINATIONS.filter(
    (d) =>
      isSidebarDestinationVisible(d, {
        isAdmin,
        isSuperAdmin,
        isFlagEnabled: resolveDestinationFlag(flagsData, flagsLoading, flagsError),
      }) && matchesPaletteQuery(d.label, search),
  ).map((d) => ({ id: d.id, label: d.label, path: d.path, icon: d.icon, sectionLabel: SECTION_LABEL_BY_ID.get(d.section), opensProBuilderWindow: d.opensProBuilderWindow }));

  // FRONT-2376: the eight System Settings sections, found the same way a
  // sidebar destination is — same admin gate `isSidebarDestinationVisible`
  // already applies to Org Settings/Sandbox in the OLD catalog (fail-closed
  // while the boot query is still loading, since `isAdmin` defaults false).
  const systemSettingsDestinations = buildSystemSettingsDestinations(isAdmin, search, (flag) =>
    resolveFeatureFlag(flag, false, flagsData, flagsLoading, flagsError),
  );

  const visibleDestinations: PaletteNavDestination[] = [...sidebarNavDestinations, ...systemSettingsDestinations];

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Fresh search box every time the palette opens — the state lives on this
  // (always-mounted) component, not on the dialog's children, so it wouldn't
  // otherwise reset just because the dialog closed.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setDebouncedSearch("");
      setSelected("");
    }
  }, [open]);

  // Debounce the query 250ms — content search hits the network, so we wait
  // for a pause before firing (same hand-rolled idiom as memory.tsx's search).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), CONTENT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const trimmedDebounced = debouncedSearch.trim();
  const contentSearchEnabled = trimmedDebounced.length >= MIN_CONTENT_QUERY_LENGTH;
  const contentQuery = contentSearchEnabled ? trimmedDebounced : "";

  const workSearch = useWorkContainerSearch(contentQuery);
  const memorySearch = useMemoryEntitySearch(contentQuery);
  const chatSearch = useChatSearch(contentQuery);
  const fileSearch = useFileSearch(contentQuery);
  const documentSearch = useDocumentSearch(contentQuery);
  /**
   * FRONT-2142. ONE read for every kind of apps row — apps, decks and
   * sketches — replacing the two gallery hooks FRONT-1974 used here.
   *
   * That earlier pairing was deliberate: sharing the galleries' cache keys let
   * the two surfaces warm each other. It did not survive a third kind. Three
   * requests per keystroke, each pulling a gallery's full page size and full
   * wire shape (Share audiences, audience locks, domain fields) to render a
   * dropdown row, is the wrong shape for a search box — so the palette now has
   * its own endpoint with its own narrow row, and the galleries keep theirs.
   *
   * `kinds` carries the flag decision to the SERVER rather than filtering the
   * answer here: a workspace with Slides off never asks for decks, so it
   * cannot render an empty Slides heading, nor one reading zero (FRONT-1974).
   */
  const decksFlagOn = useFeatureFlag(SLIDE_DECKS_FLAG, true);
  const sketchesFlagOn = useFeatureFlag(SKETCHES_FLAG, true);
  const searchKinds = useMemo(() => {
    const kinds: Array<"app" | "deck" | "sketch"> = ["app"];
    if (decksFlagOn) kinds.push("deck");
    if (sketchesFlagOn) kinds.push("sketch");
    return kinds;
  }, [decksFlagOn, sketchesFlagOn]);
  const appsSearch = useAppsSearch({
    query: contentQuery,
    kinds: searchKinds,
    enabled: contentQuery.length > 0,
  });
  // FRONT-2436: agents, scheduled jobs, skills, workflows and knowledge bases.
  // Lists the person already has, read only while searching and filtered
  // client-side with the same operators (see `command-palette-build-items`).
  const buildSources = usePaletteBuildSources(contentSearchEnabled);

  /**
   * FRONT-2391 — "Recent activity": six sections shown ABOVE Navigate only
   * while the search box is genuinely EMPTY, replaced entirely by search
   * the moment the caller types anything at all.
   *
   * `showRecents` (`shouldShowRecents`, pure and unit-tested above) gates
   * BOTH the query's `enabled` and every bit of render logic below it — and
   * it keys off the LIVE `search` value, never `!contentSearchEnabled`.
   * Those two are NOT the same condition: `contentSearchEnabled` reads the
   * DEBOUNCED value, so with one character typed — below
   * `MIN_CONTENT_QUERY_LENGTH` — or during the ~250ms window before the
   * debounce catches up to a longer query, `contentSearchEnabled` is still
   * false while `search` is not empty. Gating on `!contentSearchEnabled`
   * (the bug this fixes) left recents rendered, and `firstPaletteValue`
   * returning a recent row, for that entire window — so Enter right after
   * typing a character opened a recent item instead of doing nothing, the
   * pre-recents behaviour. `search.trim() === ""` has no such window: it
   * flips the instant a keystroke lands, same render.
   *
   * `open` is still part of the condition, for a second, unrelated reason:
   * the close effect above resets `search` (and `debouncedSearch`) to `""`,
   * which would make `search.trim() === ""` true again while the dialog is
   * CLOSED — recents must not fetch, let alone render, then. `staleTime: 0`
   * inside the hook is what makes toggling this false→true on every reopen
   * actually refetch rather than serve a stale cache entry (`use-recents.ts`).
   */
  const showRecents = shouldShowRecents(open, search);
  const recentActivity = useRecentActivity({ enabled: showRecents, kinds: searchKinds });
  const recentSections = useMemo(
    () => (showRecents ? buildRecentSections(recentActivity.data) : []),
    [showRecents, recentActivity.data],
  );
  const hasRecentResults = recentSections.length > 0;
  const recentResultsCount = showRecents
    ? recentActivity.data.chats.length +
      recentActivity.data.apps.length +
      recentActivity.data.projects.length +
      recentActivity.data.knowledge.length +
      recentActivity.data.sketches.length +
      recentActivity.data.decks.length
    : 0;
  // Same render order the JSX below actually uses (`RECENT_SECTION_ORDER`),
  // flattened to the row VALUES so `firstPaletteValue` can name whichever one
  // renders first without re-deriving the order itself.
  const recentValues = useMemo(() => {
    if (!showRecents) return [];
    const { chats, apps, projects, knowledge, sketches, decks } = recentActivity.data;
    return [
      ...chats.map((h) => `recent-chat-${h.sessionId}`),
      ...apps.map((h) => `recent-${h.kind}-${h.slug}`),
      ...projects.map((h) => `recent-project-${h.id}`),
      ...knowledge.map((h) => `recent-${h.kind}-${h.id}`),
      ...sketches.map((h) => `recent-${h.kind}-${h.slug}`),
      ...decks.map((h) => `recent-${h.kind}-${h.slug}`),
    ];
  }, [showRecents, recentActivity.data]);

  // Memoized on the query's `data` reference (TanStack keeps that reference
  // stable across renders via structural sharing when the underlying result
  // hasn't changed) — NOT rebuilt on every render, since these arrays feed
  // the selection-pin effect below and an unstable reference there re-fires
  // it on every keystroke/re-render, including ones triggered by arrow-key
  // navigation itself (FRONT-872 regression).
  const workHits = useMemo(
    () => (contentSearchEnabled ? (workSearch.data ?? []).slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, workSearch.data],
  );
  const memoryHits = useMemo(
    () => (contentSearchEnabled ? (memorySearch.data ?? []).slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, memorySearch.data],
  );
  const chatHits = useMemo(
    () => (contentSearchEnabled ? (chatSearch.data ?? []).slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, chatSearch.data],
  );
  const fileHits = useMemo(
    () => (contentSearchEnabled ? (fileSearch.data ?? []).slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, fileSearch.data],
  );
  const documentHits = useMemo(
    () => (contentSearchEnabled ? (documentSearch.data ?? []).slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, documentSearch.data],
  );
  // Three slices off the one response. The flag gate lives in `searchKinds`
  // above, so a flagged-off kind arrives empty rather than being filtered out
  // here — one decision in one place, not the same decision made twice.
  const appHits = useMemo(
    () => (contentSearchEnabled ? appsSearch.results.app.slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, appsSearch.results.app],
  );
  const deckHits = useMemo(
    () => (contentSearchEnabled ? appsSearch.results.deck.slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, appsSearch.results.deck],
  );
  const sketchHits = useMemo(
    () => (contentSearchEnabled ? appsSearch.results.sketch.slice(0, MAX_CONTENT_RESULTS) : []),
    [contentSearchEnabled, appsSearch.results.sketch],
  );

  // FRONT-2436: memoized on the source items and the query only, so arrowing
  // through the list never rebuilds it (same reason as the hit arrays above).
  const buildHits = useMemo(
    () => (contentSearchEnabled ? filterBuildItems(buildSources.items, contentQuery) : EMPTY_BUILD_ITEMS),
    [contentSearchEnabled, buildSources.items, contentQuery],
  );
  const buildBeforeAppsValues = useMemo(() => buildValues(buildHits, BUILD_KINDS_BEFORE_APPS), [buildHits]);
  const buildAfterAppsValues = useMemo(() => buildValues(buildHits, BUILD_KINDS_AFTER_APPS), [buildHits]);
  const buildCount = countBuildItems(buildHits);

  // Keyed by kind so the JSX can render the three sections from one loop
  // rather than three near-identical blocks.
  const appsSectionHits = useMemo(
    () => ({ app: appHits, deck: deckHits, sketch: sketchHits }),
    [appHits, deckHits, sketchHits],
  );

  // While a 2+ char query is pending its debounce, treat the palette as
  // loading too — otherwise the empty-state check below (which keys off the
  // debounced value) flashes "No results." before the debounce catches up.
  const searchPending = isContentSearchPending(search, debouncedSearch);

  const contentLoading =
    searchPending ||
    (contentSearchEnabled &&
      (workSearch.isLoading ||
        memorySearch.isLoading ||
        chatSearch.isLoading ||
        fileSearch.isLoading ||
        documentSearch.isLoading ||
        buildSources.isLoading));

  const showChatPage = matchesPaletteQuery("Chat", search);
  const showNotifications = matchesPaletteQuery("Notifications", search);
  // FRONT-1818: the row is titled "User Settings" (FRONT-2376) — it was
  // "Settings" before that, and "Account" between FRONT-1263 and FRONT-1818 —
  // but "account", "settings" and "profile" are what people still type, so
  // all three still find the same row.
  const showSettings =
    matchesPaletteQuery("Account", search) ||
    matchesPaletteQuery("Settings", search) ||
    matchesPaletteQuery("Profile", search);
  // FRONT-2376 review round 2 (item 10): the avatar menu's own second row,
  // added here the same way `showSettings` above stands in for "User
  // Settings" — matches its own name, so "system" (and "settings", which it
  // shares with the row above) finds it.
  const showSystemSettings = matchesPaletteQuery("System Settings", search);
  const showSignOut = matchesPaletteQuery("Sign out", search);

  const hasNavigateResults = visibleDestinations.length > 0 || showChatPage || showNotifications;
  const hasAccountResults = showSettings || showSystemSettings || showSignOut;
  const hasContentResults =
    workHits.length > 0 ||
    memoryHits.length > 0 ||
    chatHits.length > 0 ||
    fileHits.length > 0 ||
    documentHits.length > 0 ||
    appHits.length > 0 ||
    deckHits.length > 0 ||
    sketchHits.length > 0 ||
    buildCount > 0;
  const hasAnyResults =
    hasRecentResults || hasNavigateResults || hasAccountResults || hasContentResults;

  const totalResults =
    recentResultsCount +
    visibleDestinations.length +
    (showChatPage ? 1 : 0) +
    (showNotifications ? 1 : 0) +
    (showSettings ? 1 : 0) +
    (showSystemSettings ? 1 : 0) +
    (showSignOut ? 1 : 0) +
    workHits.length +
    memoryHits.length +
    chatHits.length +
    fileHits.length +
    documentHits.length +
    appHits.length +
    deckHits.length +
    sketchHits.length +
    buildCount;

  // Same render-order values the JSX below actually assigns to each row — kept
  // in lockstep with the Navigate/Account groups so `firstPaletteValue` can
  // never point at an item that isn't rendered.
  //
  // Memoized on the *inputs* that actually change the result (the typed
  // query plus the admin/super-admin gates behind `visibleDestinations`) —
  // not on `visibleDestinations` itself, which is a plain `.filter(...)` run
  // fresh every render and would defeat the memo (new array reference each
  // time even when its contents are identical).
  const staticValues = useMemo(
    () => [
      ...visibleDestinations.map((d) => `page-${d.id}`),
      ...(showChatPage ? ["page-chat"] : []),
      ...(showNotifications ? ["page-notifications"] : []),
      ...(showSettings ? ["page-account"] : []),
      ...(showSystemSettings ? ["page-system-settings"] : []),
      ...(showSignOut ? ["page-sign-out"] : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      search,
      isAdmin,
      isSuperAdmin,
      showChatPage,
      showNotifications,
      showSettings,
      showSystemSettings,
      showSignOut,
    ],
  );

  // The value of the item that would actually render first — stable across
  // renders that don't change the rendered content (see `firstPaletteValue`).
  const firstValue = useMemo(
    () =>
      firstPaletteValue({
        staticValues,
        recentValues,
        workHits,
        memoryHits,
        chatHits,
        fileHits,
        documentHits,
        appHits,
        deckHits,
        sketchHits,
        buildBeforeAppsValues,
        buildAfterAppsValues,
      }),
    [
      staticValues,
      recentValues,
      workHits,
      memoryHits,
      chatHits,
      fileHits,
      documentHits,
      appHits,
      deckHits,
      sketchHits,
      buildBeforeAppsValues,
      buildAfterAppsValues,
    ],
  );

  // Re-pin the highlighted row to the first rendered item whenever the
  // palette opens, the user types, or the top row genuinely changes content
  // (`firstValue`) — cmdk's own "auto-select first item on mount" logic
  // otherwise lets whichever of Memory/Chats/Files/Documents resolves last win the
  // highlight (FRONT-872 follow-up). `open` is included so reopening the
  // palette (search already reset to "") re-pins too, since the close-reset
  // effect below clears `selected` without necessarily changing `search`.
  //
  // Deliberately NOT keyed on `selected` — an arrow-key press changes
  // `selected` via `onValueChange` and re-renders this component; if that
  // re-render also re-fired this effect (e.g. because a dependency was an
  // unstable array rebuilt every render) it would snap the highlight straight
  // back to row 1, making arrow keys dead. `firstValue` above is memoized
  // specifically so this effect only fires on real content changes.
  useEffect(() => {
    setSelected(firstValue ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, firstValue]);

  // FRONT-874: the preview pane's document/file CONTENT fetch is gated by a
  // short local debounce of `selected` — the chrome (eyebrow/title/pills)
  // still renders instantly off `selected` itself (cheap: it's all already in
  // `chatHits`/`fileHits`/`documentHits`), but the network call for the full
  // body only fires once the highlighted row has stayed put for
  // `PREVIEW_DETAIL_DEBOUNCE_MS` — otherwise arrowing through the list would
  // fire one request per row it passed over.
  const [debouncedSelected, setDebouncedSelected] = useState(selected);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSelected(selected), PREVIEW_DETAIL_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [selected]);
  const detailArmed = debouncedSelected === selected;
  // FRONT-874 review fix: below `lg` the preview pane is CSS-hidden, not
  // unmounted, so without this the detail queries would still fire on every
  // arm even though nothing renders their result.
  const isPreviewPaneVisible = useMediaQuery(PREVIEW_PANE_MEDIA_QUERY);
  const previewDocFilename =
    detailArmed && isPreviewPaneVisible && selected.startsWith("doc-")
      ? selected.slice("doc-".length)
      : undefined;
  const previewFileId =
    detailArmed && isPreviewPaneVisible && selected.startsWith("file-")
      ? selected.slice("file-".length)
      : undefined;
  const documentDetail = useDocumentDetail(previewDocFilename);
  const fileDetail = useFileDetail(previewFileId);
  // FRONT-874 review fix: while the row is still arming (or the pane is
  // hidden), the query above is disabled so `isLoading` reads false — without
  // this, a file/doc row shows bare chrome with neither skeleton nor content
  // for the whole 150ms arming window. Treat "arming" itself as loading too.
  const previewDocLoading = selected.startsWith("doc-") && (!detailArmed || documentDetail.isLoading);
  const previewFileLoading = selected.startsWith("file-") && (!detailArmed || fileDetail.isLoading);

  function run(action: () => void) {
    setOpen(false);
    // Defer so the dialog can close before the action mutates the page.
    requestAnimationFrame(action);
  }

  // FRONT-3003: the same opener the sidebar row uses, so the palette opens the
  // window over the current page instead of landing on Home via the deep link.
  function openProBuilderWindow() {
    return () => run(() => useProBuilderOpenStore.getState().openProBuilder());
  }

  function go(path: string) {
    return () => run(() => navigate(path));
  }

  function actionForPageTarget(target: PageTarget) {
    return target.kind === "open-pro-builder" ? openProBuilderWindow() : go(target.path);
  }

  // FRONT-1938: Settings' own entry, not a plain `go(...)` — routes through
  // `useOpenSettings` so the palette's "Settings" result clears a pending
  // chat open-request the same way the avatar menu's does.
  function goSettings() {
    return () => run(() => openSettings());
  }

  // FRONT-2376 review round 2 (item 10): same reason as `goSettings` above —
  // routes through `useOpenSystemSettings` rather than a plain `go(...)` so
  // this row also clears a pending chat open-request.
  function goSystemSettings() {
    return () => run(() => openSystemSettings());
  }

  // FRONT-1550: the origin path rides on the open request itself
  // (`originToReturnTo` — null when the palette was opened from Home to
  // begin with), so closing the chat window comes back to wherever the
  // palette was invoked from. Gated like every other entry point
  // (`app-header.tsx`, `sketches.tsx`, `background-tasks.tsx`) — opening a
  // chat while already on Home or `/chat` must not navigate at all.
  function openChat(sessionId: string, title: string | null) {
    return () =>
      run(() => {
        const returnTo = originToReturnTo(location);
        if (returnTo !== null) navigate("/");
        requestOpenChat(sessionId, title, returnTo);
      });
  }

  /**
   * FRONT-874: a document result must open in the zen CHAT modal (like
   * clicking it on the Files page / Inbox), not navigate to the read-only
   * `/files/doc/:filename` page. Mirrors `openChat` above, plus the Files
   * page's own `openItem` branch (`files.tsx`): a coding-session document is
   * the resume handle for a coding session, so it reopens the coding window
   * instead — `coding-open-store`'s request is already page-agnostic (the
   * Inspector raises it the same way from elsewhere), so it just works here.
   */
  function openDocument(doc: DocumentMeta) {
    return () =>
      run(() => {
        // FRONT-1550: same origin-on-the-request + gated-navigate treatment
        // as `openChat`.
        const returnTo = originToReturnTo(location);
        if (returnTo !== null) navigate("/");
        if (doc.codingSessionId) {
          requestOpenCoding(doc.codingSessionId, returnTo);
        } else {
          requestOpenDocument(doc, returnTo);
        }
      });
  }

  /**
   * Resolves the "open" action for any palette value — the same handler a
   * row's `onSelect` fires (via Enter or double-click). Backs the preview
   * pane's own clickable "↵ Open …" affordance (FRONT-874 review fix, item
   * 3), which needs the action for the currently-*selected* item, not just
   * whichever row the mouse happens to be over. Deliberately NOT used to
   * drive the rows' own `onSelect` props above — those already carry their
   * exact tested handlers; this is a second, narrower lookup so a mismatch
   * here can only ever affect the preview pane's own button, never the
   * primary Enter/click/double-click paths on the rows themselves.
   */
  /**
   * FRONT-1974: a deck or sketch is a SERVED page, not a route inside this
   * app, so it opens in a new tab — exactly as clicking it on the Slides or
   * Sketches tab already does (`decks-gallery.tsx`). This is the only palette
   * hit type that leaves the SPA, and that asymmetry is deliberate: one
   * behaviour per object beats consistency within the palette. The preview
   * pane marks it so the jump is never a surprise.
   */
  function openServedApp(path: string) {
    return () => {
      // The `/apps/` path guard and the SYNCHRONOUS `window.open` live in
      // `~/lib/open-served-app`, shared with the Inbox's saved-app row
      // (FRONT-3339). It returns false for a path it refused: nothing opens
      // and the dialog closes.
      //
      // The open runs before the dialog closes — not through `run`, which
      // defers its action by a `requestAnimationFrame`. Every other palette
      // action is a same-tab `navigate`, which does not care; a popup opened
      // a frame after the user gesture is dropped by the browser's popup
      // blocker, and the row would silently do nothing (review panel
      // finding, FRONT-1974).
      if (!openServedAppTab(path)) {
        setOpen(false);
        return;
      }
      // The palette STAYS OPEN, unlike every other row. Those navigate this
      // tab, so leaving the dialog up over a page that just changed would be
      // wrong; a deck opens elsewhere and leaves this tab exactly as it was,
      // so closing would throw away the query for an action that changed
      // nothing here. Come back to the tab and your search is still there.
      // esc still closes it (Marko's call, live testing 2026-09-18).
    };
  }

  /**
   * FRONT-2142 — the one entry point for opening any apps-table row, routed by
   * `opensEditPage` so the destination and the preview pane's "opens in a new
   * tab" marker can never disagree about what Enter does.
   *
   * The owner of an app goes to its edit page in THIS tab, which means the
   * palette closes behind them the way it does for every other same-tab row.
   * Everyone else keeps FRONT-1974's behaviour exactly: a new tab, opened
   * synchronously so the popup blocker does not eat it, with the palette left
   * open because nothing about this tab changed.
   */
  function openAppsRow(hit: AppSearchHit) {
    if (opensEditPage(hit)) return go(`/admin/apps/${hit.slug}/edit`);
    return openServedApp(hit.path);
  }

  // FRONT-2436: every build row is a page inside the app (see each kind's
  // destination in `command-palette-build-items`), so a plain same-tab go.
  function openBuildItem(item: BuildItem) {
    return go(item.to);
  }

  function actionForValue(value: string): (() => void) | undefined {
    if (isBuildItemValue(value)) {
      const item = findBuildItem(buildHits, value);
      return item ? openBuildItem(item) : undefined;
    }
    if (value.startsWith("page-")) {
      const id = value.slice("page-".length);
      switch (id) {
        case "chat":
          return go("/chat");
        case "notifications":
          return go("/notifications");
        case "account":
          return goSettings();
        case "sign-out":
          return () =>
            run(() => {
              fetch("/api/auth/logout", { method: "POST" }).then(() => {
                window.location.href = "/login";
              });
            });
        default: {
          const target = resolvePageTarget(id);
          return target ? actionForPageTarget(target) : undefined;
        }
      }
    }
    if (value.startsWith("work-")) {
      const hit = workHits.find((w) => w.id === value.slice("work-".length));
      return hit ? go(`/work/${hit.id}`) : undefined;
    }
    if (value.startsWith("memory-")) {
      const hit = memoryHits.find((h) => h.id === value.slice("memory-".length));
      return hit ? go(memoryEntityHref(hit)) : undefined;
    }
    if (value.startsWith("chat-")) {
      const hit = chatHits.find((h) => h.sessionId === value.slice("chat-".length));
      return hit ? openChat(hit.sessionId, hit.title) : undefined;
    }
    if (value.startsWith("file-")) {
      const hit = fileHits.find((f) => f.id === value.slice("file-".length));
      return hit
        ? go(fileDetailPath(hit.id, shouldSplitFileActions(hit.kind, hit.hasBytes) ? contentQuery : ""))
        : undefined;
    }
    if (value.startsWith("doc-")) {
      const hit = documentHits.find((d) => d.filename === value.slice("doc-".length));
      return hit ? openDocument(hit) : undefined;
    }
    if (value.startsWith("app-")) {
      const hit = appHits.find((a) => a.slug === value.slice("app-".length));
      return hit ? openAppsRow(hit) : undefined;
    }
    if (value.startsWith("deck-")) {
      const hit = deckHits.find((d) => d.slug === value.slice("deck-".length));
      return hit ? openAppsRow(hit) : undefined;
    }
    if (value.startsWith("sketch-")) {
      const hit = sketchHits.find((d) => d.slug === value.slice("sketch-".length));
      return hit ? openAppsRow(hit) : undefined;
    }
    // FRONT-2391 — Recent activity. A separate `recent-` namespace (never
    // reusing e.g. "chat-"/"doc-") so a recent row's value can never collide
    // with a content-search hit's, which is what keeps cmdk's keyboard
    // selection unique between the two when both could theoretically render
    // at once. Each recent row opens through the EXACT SAME handler its
    // content-search counterpart above uses.
    if (value.startsWith("recent-chat-")) {
      const key = value.slice("recent-chat-".length);
      const hit = recentActivity.data.chats.find((h) => h.sessionId === key);
      return hit ? openChat(hit.sessionId, hit.title) : undefined;
    }
    if (value.startsWith("recent-project-")) {
      const key = value.slice("recent-project-".length);
      const hit = recentActivity.data.projects.find((h) => h.id === key);
      return hit ? go(`/work/${hit.id}`) : undefined;
    }
    if (value.startsWith("recent-file-")) {
      const key = value.slice("recent-file-".length);
      const hit = recentActivity.data.knowledge.find((h) => h.kind === "file" && h.id === key);
      // No live search term while recents are showing — always the bare
      // path, matching what `fileDetailPath` would return here regardless.
      return hit ? go(fileDetailPath(hit.id, "")) : undefined;
    }
    if (value.startsWith("recent-document-")) {
      const key = value.slice("recent-document-".length);
      const hit = recentActivity.data.knowledge.find((h) => h.kind === "document" && h.id === key);
      return hit ? openDocument(recentKnowledgeToDocumentMeta(hit)) : undefined;
    }
    if (value.startsWith("recent-app-")) {
      const key = value.slice("recent-app-".length);
      const hit = recentActivity.data.apps.find((a) => a.slug === key);
      return hit ? openAppsRow(hit) : undefined;
    }
    if (value.startsWith("recent-sketch-")) {
      const key = value.slice("recent-sketch-".length);
      const hit = recentActivity.data.sketches.find((a) => a.slug === key);
      return hit ? openAppsRow(hit) : undefined;
    }
    if (value.startsWith("recent-deck-")) {
      const key = value.slice("recent-deck-".length);
      const hit = recentActivity.data.decks.find((a) => a.slug === key);
      return hit ? openAppsRow(hit) : undefined;
    }
    return undefined;
  }

  const selectedAction = actionForValue(selected);

  /**
   * FRONT-874 review fix (interaction fix #2 — "the mouseover makes ZERO
   * sense"): a click on a row must SELECT it, not open it; only Enter or a
   * double-click opens. cmdk's own `Item` fires the identical `onSelect`
   * handler for both a click and Enter (confirmed in the installed cmdk
   * 1.1.1 source: both paths call the same internal select-and-fire
   * function), so the only way to decouple "click" from "open" is to
   * intercept the click before it reaches the Item's own bubble-phase
   * `onClick` and drive the controlled `selected` state ourselves instead.
   * A capture-phase handler on the list container does exactly that: it
   * finds the row under the pointer via cmdk's own `data-value` attribute
   * (set on every `Item`'s DOM node), sets `selected` directly, and stops
   * the event from reaching the Item's `onClick` — which is what would have
   * called `onSelect`/opened it. Enter is untouched: cmdk fires it via a
   * custom DOM event dispatched directly on the item node (not a `click`),
   * so this handler never sees it. Double-click still opens via each row's
   * own `onDoubleClick={onSelect}` (see `PaletteRow`) — a `dblclick` is a
   * distinct event this handler doesn't intercept.
   *
   * FRONT-2391 product correction (fix round 3): that select-then-open
   * separation only makes sense when there is a preview pane to look at
   * first. While Recent activity is showing (`showRecents` — see the
   * one-column layout below) there is none, so select-then-open would leave
   * a click doing nothing visible. `shouldInterceptRowClick` (above) is the
   * decision; when it says false, this function does nothing at all and
   * lets the click fall through to cmdk's own `Item`, which both sets
   * `selected` AND fires the row's `onSelect` (its open handler) in one go
   * — the exact same dispatch Enter/double-click already use, never a
   * second, hand-rolled one.
   */
  function handleListClickCapture(e: MouseEvent<HTMLDivElement>) {
    if (!shouldInterceptRowClick(showRecents)) return;
    const itemEl = (e.target as HTMLElement).closest<HTMLElement>("[data-value]");
    if (!itemEl) return;
    const value = itemEl.getAttribute("data-value");
    if (!value) return;
    e.stopPropagation();
    setSelected(value);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search pages, projects, memory, chats, files, documents, agents, scheduled jobs, skills,
          workflows, and knowledge bases.
        </DialogDescription>
        <style>{`
          @keyframes eddy-palette-bg-in { from { opacity: 0 } to { opacity: 1 } }
          @keyframes eddy-palette-panel-in {
            from { opacity: 0; transform: translateY(8px) }
            to   { opacity: 1; transform: translateY(0) }
          }
        `}</style>
        {/*
         * A single fixed full-viewport layer acts as BOTH the blurred
         * backdrop and the flex-centering container for the card — mirroring
         * the hand-rolled zen chat modal (inbox-chat-modal.tsx) rather than
         * the shadcn `DialogOverlay`/`DialogContent` pair, whose hardcoded
         * centering/sizing classes fight this surface's split layout.
         *
         * Closing on backdrop click goes through `useBackdropDismiss` (keys
         * off where the press STARTED — see that hook). Escape-to-close and
         * the focus trap still come for free from Radix's `Dialog.Content`.
         */}
        <DialogPrimitive.Content
          {...backdropDismiss}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto outline-none"
          style={{
            padding: "56px 24px",
            background: "rgba(20, 20, 26, 0.42)",
            backdropFilter: "blur(10px) saturate(0.9)",
            WebkitBackdropFilter: "blur(10px) saturate(0.9)",
            animation: "eddy-palette-bg-in 220ms ease-out",
          }}
        >
          <div
            className="flex w-full flex-col overflow-hidden border border-border bg-card"
            style={{
              maxWidth: 1120,
              // FRONT-874 review fix (item 4): a fixed, stately height rather
              // than a shrink-to-content `maxHeight` — the surface must stay
              // the same size whether a query returns 1 result or 20, matching
              // the approved mockup's proportions instead of collapsing into
              // a shallow strip.
              height: "min(640px, calc(100vh - 112px))",
              borderRadius: "var(--radius-xl-ds)",
              boxShadow: "var(--shadow-lg)",
              animation: "eddy-palette-panel-in 240ms ease-out",
            }}
          >
            <CommandPrimitive
              shouldFilter={false}
              value={selected}
              onValueChange={setSelected}
              // FRONT-874 review fix (item 1 — "the mouseover makes ZERO
              // sense"): cmdk's default pointer-move selection flips the
              // highlight (and this pane's preview) on mere hover. Disabling
              // it means selection changes only via arrow keys and the
              // click-capture handler below — hover paints nothing but its
              // own faint background (see `PaletteRow`).
              disablePointerSelection
              className="flex min-h-0 flex-1 flex-col overflow-hidden text-foreground"
            >
              {/* Header: E mark, search icon, borderless search input, esc hint. */}
              <div className="flex shrink-0 items-center gap-3 border-b border-border px-[22px] py-3.5">
                <PaletteMark />
                <MagnifyingGlassIcon size={17} className="shrink-0 text-muted-foreground" />
                <CommandPrimitive.Input
                  value={search}
                  onValueChange={setSearch}
                  // FRONT-901: an empty search box is the one moment someone
                  // is looking straight at this field with nothing else to
                  // read — so the placeholder spends that space teaching the
                  // operators rather than restating what a search box is for.
                  // Wording is the product owner's own.
                  placeholder={
                    'You can search with boolean operators - "exact keyword", OR, AND, and "-" to exclude'
                  }
                  // Without this the field has NO accessible name at all:
                  // cmdk's combobox does not fall back to the placeholder,
                  // so a screen reader announced an unlabelled text box.
                  aria-label="Search"
                  className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-[length:var(--text-lg)] text-foreground outline-none placeholder:text-muted-foreground"
                />
                <span className="shrink-0 rounded-md border border-border bg-muted px-2 py-1 font-mono text-[length:var(--text-xs)] text-muted-foreground">
                  esc
                </span>
              </div>

              {/* Split body: sectioned result list left, live preview right —
               * except while Recent activity is showing (`showRecents`),
               * where there is no preview to show at all (product
               * correction, FRONT-2391 fix round 3): the list alone fills
               * the full width, at every breakpoint, and the `lg:w-[420px]`
               * cap that makes room for the preview column is dropped along
               * with it. The dialog's own outer size never changes either
               * way, so nothing jumps when the layout switches back the
               * instant a character is typed. */}
              <div className="flex min-h-0 flex-1">
                <div
                  className={
                    showRecents
                      ? "flex min-h-0 w-full flex-col overflow-hidden"
                      : "flex min-h-0 w-full flex-col overflow-hidden lg:w-[420px] lg:shrink-0 lg:border-r lg:border-border"
                  }
                >
                  <CommandList
                    className="max-h-none h-full p-0"
                    onClickCapture={handleListClickCapture}
                  >
                    <div className="flex flex-col gap-5" style={{ padding: "18px 14px 18px 18px" }}>
                      {/* FRONT-2391 — Recent activity, ABOVE Navigate, only
                        * while the search box is empty AND the palette is
                        * open — see `showRecents`'s own comment above. While
                        * the first request is in flight every section's
                        * array is empty, so nothing renders here at all: no
                        * spinner, no skeleton, per the contract ("no spinner
                        * flash"). */}
                      {showRecents && (
                        <RecentChatsSection
                          hits={recentActivity.data.chats}
                          openRow={(hit) => openChat(hit.sessionId, hit.title)}
                        />
                      )}
                      {showRecents && (
                        <RecentAppsSection
                          hits={recentActivity.data.apps}
                          testId={RECENT_SECTION_TEST_ID.apps}
                          heading={RECENT_SECTION_HEADING.apps}
                          openRow={openAppsRow}
                        />
                      )}
                      {showRecents && (
                        <RecentProjectsSection
                          hits={recentActivity.data.projects}
                          openRow={(hit) => go(`/work/${hit.id}`)}
                        />
                      )}
                      {showRecents && (
                        <RecentKnowledgeSection
                          hits={recentActivity.data.knowledge}
                          openRow={(hit) =>
                            hit.kind === "file"
                              ? go(fileDetailPath(hit.id, ""))
                              : openDocument(recentKnowledgeToDocumentMeta(hit))
                          }
                        />
                      )}
                      {showRecents && (
                        <RecentAppsSection
                          hits={recentActivity.data.sketches}
                          testId={RECENT_SECTION_TEST_ID.sketches}
                          heading={RECENT_SECTION_HEADING.sketches}
                          openRow={openAppsRow}
                        />
                      )}
                      {showRecents && (
                        <RecentAppsSection
                          hits={recentActivity.data.decks}
                          testId={RECENT_SECTION_TEST_ID.decks}
                          heading={RECENT_SECTION_HEADING.decks}
                          openRow={openAppsRow}
                        />
                      )}

                      {!hasAnyResults && !contentLoading && (
                        <div className="px-2 py-10 text-center text-sm text-muted-foreground">
                          No results.
                        </div>
                      )}
                      {!hasAnyResults && contentLoading && (
                        <div className="px-2 py-10 text-center text-sm text-muted-foreground">
                          Searching…
                        </div>
                      )}

                      {hasNavigateResults && (
                        <PaletteSection
                          label="Navigate"
                          count={
                            visibleDestinations.length +
                            (showChatPage ? 1 : 0) +
                            (showNotifications ? 1 : 0)
                          }
                        >
                          {visibleDestinations.map((dest) => (
                            <PaletteRow
                              key={dest.id}
                              value={`page-${dest.id}`}
                              icon={dest.icon}
                              title={<HighlightedTitle text={dest.label} query={search} />}
                              meta={<RowMeta>{dest.sectionLabel}</RowMeta>}
                              onSelect={actionForPageTarget(pageTargetForDestination(dest))}
                            />
                          ))}
                          {/* Not sidebar destinations — kept as hand-added entries. */}
                          {showChatPage && (
                            <PaletteRow
                              value="page-chat"
                              icon={ChatCircleIcon}
                              title={<HighlightedTitle text="Chat" query={search} />}
                              onSelect={go("/chat")}
                            />
                          )}
                          {showNotifications && (
                            <PaletteRow
                              value="page-notifications"
                              icon={BellIcon}
                              title={<HighlightedTitle text="Notifications" query={search} />}
                              onSelect={go("/notifications")}
                            />
                          )}
                        </PaletteSection>
                      )}

                      {hasAccountResults && (
                        <PaletteSection
                          label="Account"
                          count={
                            (showSettings ? 1 : 0) +
                            (showSystemSettings ? 1 : 0) +
                            (showSignOut ? 1 : 0)
                          }
                        >
                          {showSettings && (
                            <PaletteRow
                              value="page-account"
                              icon={GearIcon}
                              title={<HighlightedTitle text="User Settings" query={search} />}
                              onSelect={goSettings()}
                            />
                          )}
                          {showSystemSettings && (
                            <PaletteRow
                              value="page-system-settings"
                              icon={SlidersHorizontalIcon}
                              title={<HighlightedTitle text="System Settings" query={search} />}
                              onSelect={goSystemSettings()}
                            />
                          )}
                          {showSignOut && (
                            <PaletteRow
                              value="page-sign-out"
                              icon={SignOutIcon}
                              title={<HighlightedTitle text="Sign out" query={search} />}
                              onSelect={() =>
                                run(() => {
                                  fetch("/api/auth/logout", { method: "POST" }).then(() => {
                                    window.location.href = "/login";
                                  });
                                })
                              }
                            />
                          )}
                        </PaletteSection>
                      )}

                      {workHits.length > 0 && (
                        <PaletteSection label="Projects" count={workHits.length}>
                          {workHits.map((hit) => (
                            <PaletteRow
                              key={hit.id}
                              value={`work-${hit.id}`}
                              icon={BriefcaseIcon}
                              title={<HighlightedTitle text={entityHitDisplayName(hit)} query={search} />}
                              snippet="Project"
                              onSelect={go(`/work/${hit.id}`)}
                            />
                          ))}
                        </PaletteSection>
                      )}

                      {memoryHits.length > 0 && (
                        <PaletteSection label="Memory" count={memoryHits.length}>
                          {memoryHits.map((hit) => (
                            <PaletteRow
                              key={hit.id}
                              value={`memory-${hit.id}`}
                              // FRONT-876 review fix: distinct from the
                              // Documents section's BrainIcon two rows below
                              // — the two used to share one icon, reading as
                              // one section visually split in half.
                              icon={AddressBookIcon}
                              title={<HighlightedTitle text={entityHitDisplayName(hit)} query={search} />}
                              snippet={memoryHitSubtitle(hit)}
                              onSelect={go(memoryEntityHref(hit))}
                            />
                          ))}
                        </PaletteSection>
                      )}

                      {chatHits.length > 0 && (
                        <PaletteSection label="Chats" count={chatHits.length}>
                          {chatHits.map((hit) => (
                            <PaletteRow
                              key={hit.sessionId}
                              value={`chat-${hit.sessionId}`}
                              icon={ChatCircleIcon}
                              title={<HighlightedTitle text={hit.title || "Untitled chat"} query={search} />}
                              snippet={hit.snippet ? <HighlightedSnippet snippet={hit.snippet} /> : undefined}
                              meta={<RowMeta>{formatShortDate(hit.lastActiveAt ?? hit.updatedAt)}</RowMeta>}
                              onSelect={openChat(hit.sessionId, hit.title)}
                            />
                          ))}
                        </PaletteSection>
                      )}

                      {(fileHits.length > 0 || documentHits.length > 0) && (
                        <PaletteSection label="Files" count={fileHits.length + documentHits.length}>
                          {fileHits.map((file) => (
                            <PaletteRow
                              key={file.id}
                              value={`file-${file.id}`}
                              icon={FilesIcon}
                              title={<HighlightedTitle text={file.name} query={search} />}
                              snippet={
                                file.snippet ? (
                                  <HighlightedSnippet snippet={file.snippet} />
                                ) : file.description ? (
                                  <HighlightedQueryText text={file.description} query={search} />
                                ) : undefined
                              }
                              meta={<RowMeta>{formatShortDate(file.updatedAt)}</RowMeta>}
                              onSelect={go(
                                fileDetailPath(
                                  file.id,
                                  shouldSplitFileActions(file.kind, file.hasBytes) ? contentQuery : "",
                                ),
                              )}
                            />
                          ))}
                          {documentHits.map((doc) => (
                            <PaletteRow
                              key={doc.filename}
                              value={`doc-${doc.filename}`}
                              icon={FilesIcon}
                              title={<HighlightedTitle text={doc.title || doc.filename} query={search} />}
                              snippet={
                                doc.snippet ? (
                                  <HighlightedSnippet snippet={doc.snippet} />
                                ) : doc.summary ? (
                                  <HighlightedQueryText text={stripMarkdownPeek(doc.summary)} query={search} />
                                ) : undefined
                              }
                              meta={<RowMeta>{formatShortDate(doc.updatedAt)}</RowMeta>}
                              onSelect={openDocument(doc)}
                            />
                          ))}
                        </PaletteSection>
                      )}

                      {/* FRONT-2436: knowledge bases sit with Files — both
                        * live on the Knowledge page — ahead of Apps. */}
                      {BUILD_KINDS_BEFORE_APPS.map((kind) => (
                        <BuildItemSection
                          key={kind}
                          kind={kind}
                          items={buildHits[kind]}
                          query={search}
                          openItem={openBuildItem}
                        />
                      ))}

                      {/* FRONT-2142: one renderer for all three apps-table
                        * kinds, in the same order `firstPaletteValue` names
                        * them — a second copy per kind is exactly how the two
                        * orders drift, and a drifted order makes Enter act on
                        * a row other than the highlighted one. */}
                      {APPS_SECTION_ORDER.map((kind) => {
                        const hits = appsSectionHits[kind];
                        if (hits.length === 0) return null;
                        return (
                          <PaletteSection
                            key={kind}
                            label={APP_KIND_SECTION_LABEL[kind]}
                            count={hits.length}
                          >
                            {hits.map((hit) => (
                              <PaletteRow
                                key={hit.slug}
                                value={`${kind}-${hit.slug}`}
                                icon={APP_KIND_ICON[kind]}
                                title={<HighlightedTitle text={hit.title} query={search} />}
                                snippet={
                                  hit.description ? (
                                    <HighlightedQueryText text={hit.description} query={search} />
                                  ) : undefined
                                }
                                meta={<RowMeta>{formatShortDate(hit.updatedAt)}</RowMeta>}
                                onSelect={openAppsRow(hit)}
                              />
                            ))}
                          </PaletteSection>
                        );
                      })}

                      {/* FRONT-2436: the sidebar's Build group, in its order,
                        * after Apps — the order `firstPaletteValue` names. */}
                      {BUILD_KINDS_AFTER_APPS.map((kind) => (
                        <BuildItemSection
                          key={kind}
                          kind={kind}
                          items={buildHits[kind]}
                          query={search}
                          openItem={openBuildItem}
                        />
                      ))}
                    </div>
                  </CommandList>
                </div>

                {/* FRONT-874 fidelity fix: `/40` read as plain white next to
                 * the list — bumped to `/70` so the pane reads as a visibly
                 * distinct (but still subtle) surface in both themes, per the
                 * mockup's split-pane look. The excerpt card underneath keeps
                 * `bg-card` (see `ExcerptCard`) so it still pops against this
                 * tinted background.
                 *
                 * FRONT-2391 fix round 3 (product correction): while Recent
                 * activity is showing, this whole container is omitted from
                 * the DOM — not merely CSS-hidden — since there is nothing
                 * to preview (a highlighted recent row has no preview
                 * branch any more; see `PalettePreviewPane`). It reappears
                 * the instant `showRecents` flips false, i.e. the moment
                 * anything is typed. `data-testid` lets the browser spec
                 * assert both halves of that directly. */}
                {!showRecents && (
                  <div
                    data-testid="palette-preview-pane"
                    className="hidden min-h-0 flex-1 overflow-y-auto bg-muted/70 lg:block"
                    style={{ padding: "34px 38px" }}
                  >
                    <PalettePreviewPane
                      selected={selected}
                      workHits={workHits}
                      memoryHits={memoryHits}
                      chatHits={chatHits}
                      fileHits={fileHits}
                      documentHits={documentHits}
                      appHits={appHits}
                      deckHits={deckHits}
                      sketchHits={sketchHits}
                      buildHits={buildHits}
                      fileDetail={{ data: fileDetail.data, isLoading: previewFileLoading }}
                      documentDetail={{ data: documentDetail.data, isLoading: previewDocLoading }}
                      onOpen={selectedAction}
                    />
                  </div>
                )}
              </div>

              {/* Footer: kbd hints left, live result count right. */}
              <div className="flex shrink-0 flex-wrap items-center gap-4 border-t border-border bg-muted/40 px-[22px] py-2">
                <FooterHint keys="↑↓" label="navigate" />
                <FooterHint keys="↵" label="open" />
                <FooterHint keys="esc" label="close" />
                {/* FRONT-901: the operators only exist if people can find
                    them. Three quiet hints in the footer, no advanced-search
                    form — the professionalism lives in the single input.
                    Hidden on a narrow palette, where six hints plus the result
                    count would wrap onto a second line. */}
                <FooterHint
                  keys={'"…"'}
                  label="exact"
                  srLabel="Wrap words in double quotes to match that exact phrase"
                  className="hidden sm:inline-flex"
                />
                <FooterHint
                  keys="OR"
                  label="either"
                  srLabel="Put upper-case OR between two words to match either one"
                  className="hidden sm:inline-flex"
                />
                <FooterHint
                  keys="-word"
                  label="exclude"
                  srLabel="Put a minus in front of a word to exclude it"
                  className="hidden sm:inline-flex"
                />
                <div className="flex-1" />
                <span className="font-mono text-[length:var(--text-2xs)] text-[var(--ink-3)]">
                  {totalResults} {totalResults === 1 ? "result" : "results"}
                </span>
              </div>
            </CommandPrimitive>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
